/* eslint-disable */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Brain, X, Package as PackageIcon, MoreVertical, Settings, UserPlus, Download, CheckCircle, FileText, Trash2, Eye, Edit, ChevronDown } from 'lucide-react';
import { toast } from 'react-hot-toast';
import TenantConfiguration from './TenantConfiguration';

const TenantManagement = () => {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [activeModal, setActiveModal] = useState(null);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [showAiConfigModal, setShowAiConfigModal] = useState(false);
  const [selectedTenantForAiConfig, setSelectedTenantForAiConfig] = useState(null);
  const { user } = useAuth();

  // Form states
  const [newTenant, setNewTenant] = useState({
    tenantCode: '',
    tenantName: '',
    description: '',
    databaseName: '',
    databaseType: 'sqlserver', // Will be updated from config
    databaseServer: 'localhost',
    databaseUsername: 'sa',
    databasePassword: '',
    databasePort: '',
    uploadDirectory: '',
    isDefault: false,
    // Enhanced tenant configuration fields
    companyName: '',
    companyLogo: '',
    adminEmail: '',
    primaryContact: '',
    phoneNumber: '',
    address: '',
    website: '',
    timezone: 'UTC', // Will be updated from config
    currency: 'USD', // Will be updated from config
    language: 'en', // Will be updated from config
    brandColors: {
      primary: '#007bff',
      secondary: '#6c757d',
      accent: '#28a745'
    },
    features: {
      enableNotifications: true,
      enableIntegrations: true,
      enableCustomReports: false,
      enableAPIAccess: false
    },
    subscription: {
      plan: 'Standard', // Will be updated from config
      maxUsers: 10, // Will be updated from config
      storageLimit: '5GB', // Will be updated from config
      expiryDate: ''
    }
  });
  const [userAssignment, setUserAssignment] = useState({
    userId: '',
    accessLevel: 'Read',
    isDefault: false
  });

  // Edit tenant state
  const [editTenant, setEditTenant] = useState({
    tenantCode: '',
    tenantName: '',
    description: '',
    companyName: '',
    companyLogo: '',
    adminEmail: '',
    primaryContact: '',
    phoneNumber: '',
    address: '',
    website: '',
    timezone: 'UTC',
    currency: 'USD',
    language: 'en',
    databaseType: 'sqlserver',
    databaseServer: 'localhost',
    databaseName: '',
    databaseUsername: 'sa',
    databasePassword: '',
    databasePort: '1433',
    uploadDirectory: '',
    isActive: true
  });

  // Import users state
  const [importProgress, setImportProgress] = useState({
    isImporting: false,
    progress: 0,
    total: 0,
    imported: 0,
    failed: 0,
    errors: []
  });

  // Database validation state
  const [dbValidation, setDbValidation] = useState({
    isValidating: false,
    isInitializing: false,
    isFixing: false,
    isAddingExtra: false,
    isDroppingExtra: false,
    status: null, // 'valid', 'invalid', 'missing', 'error'
    expandedTables: [], // Track which tables are expanded in the list
    results: {
      tablesChecked: 0,
      tablesValid: 0,
      tablesMissing: 0,
      tablesCreated: 0,
      tablesFailed: 0,
      columnsChecked: 0,
      columnsValid: 0,
      columnsMissing: 0,
      columnsMismatched: 0,
      columnsExtra: 0,
      extraColumns: {},
      columnsFixed: 0,
      columnsFailed: 0,
      columnsAddedToSchema: 0,
      columnsDropped: 0,
      seedDataInserted: false,
      errors: [],
      warnings: [],
      tableDetails: {},
      // Detailed fix results
      tableResults: {}, // Detailed results per table
      tableErrors: [], // Errors from table creation
      fixErrors: [] // Errors from column fixes
    }
  });

  // Unicode validation state
  const [unicodeValidation, setUnicodeValidation] = useState({
    isValidating: false,
    isFixing: false,
    status: null, // 'valid', 'invalid', 'fixed', 'error'
    results: {
      databaseCollation: '',
      columnDetails: {},
      unicodeTestPassed: false,
      corruptionFound: false,
      corruptedRecords: [],
      fixApplied: false,
      errors: [],
      warnings: []
    }
  });

  // Tenant database status tracking
  const [tenantDbStatus, setTenantDbStatus] = useState({});
  const [validatingAll, setValidatingAll] = useState(false);
  
  // Email domain mapping states
  const [emailDomainMappings, setEmailDomainMappings] = useState([]);
  const [emailDomainModal, setEmailDomainModal] = useState({
    isOpen: false,
    tenantCode: '',
    emailDomain: '',
    isEditing: false
  });
  const [testEmail, setTestEmail] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [pendingRestoreTenant, setPendingRestoreTenant] = useState(null);
  const [packages, setPackages] = useState([]);
  const [showPackageModal, setShowPackageModal] = useState(false);
  const [selectedTenantForPackage, setSelectedTenantForPackage] = useState(null);
  const [tenantPackages, setTenantPackages] = useState({});
  const [expandedMenu, setExpandedMenu] = useState({});
  const [tenantConfig, setTenantConfig] = useState({
    timezones: [],
    currencies: [],
    languages: [],
    subscriptionPlans: [],
    storageLimits: [],
    databaseTypes: [],
    defaults: {}
  });

  const loadPackages = async () => {
    try {
      const response = await axios.get('/api/packages?activeOnly=true');
      if (response.data.success) {
        setPackages(response.data.data);
      }
    } catch (error) {
      console.error('Error loading packages:', error);
    }
  };

  const loadTenantPackage = async (tenantCode) => {
    try {
      const response = await axios.get(`/api/packages/tenant/${tenantCode}`);
      if (response.data.success) {
        setTenantPackages(prev => ({
          ...prev,
          [tenantCode]: response.data.data
        }));
      }
    } catch (error) {
      // Tenant might not have a package assigned
      setTenantPackages(prev => ({
        ...prev,
        [tenantCode]: null
      }));
    }
  };

  const handleAssignPackage = async (tenantId, packageId) => {
    try {
      await axios.post(`/api/packages/${packageId}/assign`, { tenantId });
      toast.success('Package assigned successfully');
      setShowPackageModal(false);
      setSelectedTenantForPackage(null);
      // Reload tenant package info
      if (selectedTenantForPackage) {
        await loadTenantPackage(selectedTenantForPackage.code);
      }
      await loadTenants();
    } catch (error) {
      console.error('Error assigning package:', error);
      toast.error(error.response?.data?.error || 'Failed to assign package');
    }
  };

  const handleOpenPackageModal = (tenant) => {
    setSelectedTenantForPackage(tenant);
    loadTenantPackage(tenant.code);
    setShowPackageModal(true);
  };

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    loadTenants();
    loadUsers();
    loadEmailDomainMappings();
    loadPackages();
    loadTenantConfig();
  }, [user]);

  const loadTenantConfig = async () => {
    try {
      const response = await axios.get('/api/tenants/config/options');
      if (response.data.success && response.data.data) {
        const config = response.data.data;
        setTenantConfig(config);
        
        // Update default values in newTenant state if defaults are available
        if (config.defaults) {
          setNewTenant(prev => ({
            ...prev,
            timezone: config.defaults.timezone || prev.timezone,
            currency: config.defaults.currency || prev.currency,
            language: config.defaults.language || prev.language,
            databaseType: config.defaults.databaseType || prev.databaseType,
            subscription: {
              ...prev.subscription,
              plan: config.defaults.subscriptionPlan || prev.subscription.plan,
              maxUsers: config.defaults.maxUsers || prev.subscription.maxUsers,
              storageLimit: config.defaults.storageLimit || prev.subscription.storageLimit
            },
            // Update database defaults based on database type
            databaseUsername: config.databaseTypes?.find(dt => dt.value === (config.defaults.databaseType || 'sqlserver'))?.defaultUsername || prev.databaseUsername,
            databasePort: config.databaseTypes?.find(dt => dt.value === (config.defaults.databaseType || 'sqlserver'))?.defaultPort?.toString() || prev.databasePort
          }));
        }
      }
    } catch (error) {
      console.error('Error loading tenant configuration:', error);
      // Set default values if config fails to load
      setTenantConfig({
        timezones: [{ value: 'UTC', label: 'UTC' }],
        currencies: [{ value: 'USD', label: 'USD - US Dollar' }],
        languages: [{ value: 'en', label: 'English' }],
        subscriptionPlans: [{ value: 'Standard', label: 'Standard' }],
        storageLimits: [{ value: '5GB', label: '5 GB' }],
        databaseTypes: [{ value: 'sqlserver', label: 'SQL Server' }],
        defaults: {
          timezone: 'UTC',
          currency: 'USD',
          language: 'en',
          subscriptionPlan: 'Standard',
          maxUsers: 10,
          storageLimit: '5GB',
          databaseType: 'sqlserver'
        }
      });
    }
  };

  // Load tenant packages when tenants are loaded
  useEffect(() => {
    if (tenants.length > 0) {
      tenants.forEach(tenant => {
        loadTenantPackage(tenant.code);
      });
    }
  }, [tenants]);

  // Auto-validate databases when tenants are loaded (only once)
  useEffect(() => {
    if (tenants.length > 0) {
      // Auto-validate all databases after a short delay, but only if not already validating
      const timer = setTimeout(() => {
        if (!validatingAll) {
          handleValidateAllDatabases();
        }
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, [tenants.length]); // Remove validatingAll from dependencies to prevent infinite loop

  const loadTenants = async () => {
    try {
      const response = await axios.get('/api/tenants?includeInactive=true');
      const data = response.data;
      const tenantsData = (data && (data.tenants || data.data?.tenants)) || (Array.isArray(data) ? data : []);
      setTenants(tenantsData);
    } catch (error) {
      console.error('Error loading tenants:', error);
      setError('Failed to load tenants');
    }
  };

  const loadUsers = async () => {
    try {
      const response = await axios.get('/api/auth/users');
      const data = response.data;
      const usersData = data?.users || data?.data?.users || [];
      setUsers(usersData);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadEmailDomainMappings = async () => {
    try {
      const response = await axios.get('/api/email-domain-mappings', {
      });

      if (!response.ok) {
        throw new Error('Failed to load email domain mappings');
      }

      const data = response.data;
      setEmailDomainMappings(data.mappings || data.data?.mappings || []);
    } catch (error) {
      console.error('Error loading email domain mappings:', error);
    }
  };

  const handleSetEmailDomainMapping = async (tenantCode, emailDomain) => {
    try {
      const response = await axios.post(`/api/email-domain-mappings/${tenantCode}`, { emailDomain });

      await loadEmailDomainMappings();
      setSuccessMessage('Email domain mapping set successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error setting email domain mapping:', error);
      setError(error.response?.data?.error || error.message);
    }
  };

  const handleRemoveEmailDomainMapping = async (tenantCode) => {
    try {
      await axios.delete(`/api/email-domain-mappings/${tenantCode}`);

      await loadEmailDomainMappings();
      setSuccessMessage('Email domain mapping removed successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error removing email domain mapping:', error);
      setError(error.response?.data?.error || error.message);
    }
  };

  const handleTestEmailDomainMapping = async () => {
    if (!testEmail) {
      setError('Please enter an email address to test');
      return;
    }

    try {
      const response = await axios.post('/api/email-domain-mappings/test', { email: testEmail });
      setTestResult(response.data);
    } catch (error) {
      console.error('Error testing email domain mapping:', error);
      setError(error.response?.data?.error || error.message);
    }
  };

  const handleRestoreTenant = async () => {
    if (!pendingRestoreTenant) return;

    setLoading(true);
    try {
      await axios.post(`/api/tenants/${pendingRestoreTenant}/restore`, newTenant);

      setPendingRestoreTenant(null);
      setActiveModal(null);
      await loadTenants();
      setSuccessMessage('Tenant restored successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);

    } catch (error) {
      console.error('Error restoring tenant:', error);
      setError(error.response?.data?.error || error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTenant = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post('/api/tenants', newTenant);

      // Reset form with config defaults
      const defaults = tenantConfig.defaults || {};
      const defaultDbType = tenantConfig.databaseTypes?.find(dt => dt.value === (defaults.databaseType || 'sqlserver'));
      setNewTenant({
        tenantCode: '',
        tenantName: '',
        description: '',
        databaseName: '',
        databaseType: defaults.databaseType || 'sqlserver',
        databaseServer: 'localhost',
        databaseUsername: defaultDbType?.defaultUsername || 'sa',
        databasePassword: '',
        databasePort: defaultDbType?.defaultPort?.toString() || '1433',
        uploadDirectory: '',
        isDefault: false,
        companyName: '',
        companyLogo: '',
        adminEmail: '',
        primaryContact: '',
        phoneNumber: '',
        address: '',
        website: '',
        timezone: defaults.timezone || 'UTC',
        currency: defaults.currency || 'USD',
        language: defaults.language || 'en',
        brandColors: {
          primary: '#007bff',
          secondary: '#6c757d',
          accent: '#28a745'
        },
        features: {
          enableNotifications: true,
          enableIntegrations: true,
          enableCustomReports: false,
          enableAPIAccess: false
        },
        subscription: {
          plan: defaults.subscriptionPlan || 'Standard',
          maxUsers: defaults.maxUsers || 10,
          storageLimit: defaults.storageLimit || '5GB',
          expiryDate: ''
        }
      });

      setActiveModal(null);
      await loadTenants();
      setSuccessMessage('Tenant created successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);

    } catch (error) {
      console.error('Error creating tenant:', error);
      const errorMessage = error.response?.data?.error || error.message;
      
      // Check if it's a previously deleted tenant error
      if (errorMessage && errorMessage.includes('was previously deleted')) {
        setError(`${errorMessage} Would you like to restore it instead?`);
        // Store the tenant code for potential restoration
        setPendingRestoreTenant(newTenant.tenantCode);
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAssignUser = async (e) => {
    e.preventDefault();
    if (!selectedTenant) return;

    setLoading(true);

    try {
      const response = await axios.post(`/api/tenants/${selectedTenant.code}/users`, userAssignment);
      
      const result = response.data;
      
      // Show appropriate success message based on the response
      if (result.assignment?.wasExisting) {
        if (result.assignment.previousAccessLevel !== result.assignment.accessLevel) {
          setSuccessMessage(`User access level updated from ${result.assignment.previousAccessLevel} to ${result.assignment.accessLevel}`);
        } else {
          setSuccessMessage('User is already assigned to this tenant with the same access level');
        }
      } else {
        setSuccessMessage('User assigned to tenant successfully!');
      }

      // Reset form
      setUserAssignment({
        userId: '',
        accessLevel: 'Read',
        isDefault: false
      });

      setActiveModal(null);
      await loadTenants();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);

    } catch (error) {
      console.error('Error assigning user:', error);
      setError(error.response?.data?.error || error.message);
    } finally {
      setLoading(false);
    }
  };

  const getTenantStats = async (tenantCode) => {
    try {
      const response = await axios.get(`/api/tenants/${tenantCode}/statistics`);
      return response.data;
    } catch (error) {
      console.error('Error getting tenant stats:', error);
      return null;
    }
  };

  // Function to populate edit tenant form
  const openEditModal = (tenant) => {
    setEditTenant({
      tenantCode: tenant.code || '',
      tenantName: tenant.name || '',
      description: tenant.description || '',
      companyName: tenant.companyName || '',
      companyLogo: tenant.companyLogo || '',
      adminEmail: tenant.adminEmail || '',
      primaryContact: tenant.primaryContact || '',
      phoneNumber: tenant.phoneNumber || '',
      address: tenant.address || '',
      website: tenant.website || '',
      timezone: tenant.timezone || 'UTC',
      currency: tenant.currency || 'USD',
      language: tenant.language || 'en',
      databaseType: tenant.databaseType || 'sqlserver',
      databaseServer: tenant.databaseServer || 'localhost',
      databaseName: tenant.databaseName || '',
      databaseUsername: tenant.databaseUsername || 'sa',
      databasePassword: tenant.databasePassword || '',
      databasePort: tenant.databasePort || '1433',
      uploadDirectory: tenant.uploadDirectory || '',
      isActive: tenant.isActive !== undefined ? tenant.isActive : true
    });
    setSelectedTenant(tenant);
    setActiveModal('edit');
  };

  // Edit tenant handler
  const handleEditTenant = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.put(`/api/tenants/${selectedTenant.code}`, editTenant);

      setActiveModal(null);
      await loadTenants();
      setSuccessMessage('Tenant updated successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);

    } catch (error) {
      console.error('Error updating tenant:', error);
      setError(error.response?.data?.error || error.message);
    } finally {
      setLoading(false);
    }
  };

  // Delete tenant handler
  const handleDeleteTenant = async () => {
    setLoading(true);

    try {
      await axios.delete(`/api/tenants/${selectedTenant.code}`);

      setActiveModal(null);
      setSelectedTenant(null);
      await loadTenants();
      setSuccessMessage('Tenant deleted successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);

    } catch (error) {
      console.error('Error deleting tenant:', error);
      setError(error.response?.data?.error || error.message);
    } finally {
      setLoading(false);
    }
  };

  // Import users from tenant database
  const handleImportUsers = async () => {
    setImportProgress({
      isImporting: true,
      progress: 0,
      total: 0,
      imported: 0,
      failed: 0,
      errors: []
    });

    try {
      const response = await axios.post(`/api/tenants/${selectedTenant.code}/import-users`);
      
      const result = response.data;
      
      setImportProgress({
        isImporting: false,
        progress: 100,
        total: result.total || 0,
        imported: result.imported || 0,
        failed: result.failed || 0,
        errors: result.errors || []
      });

      // Refresh users list
      await loadUsers();

    } catch (error) {
      console.error('Error importing users:', error);
      setImportProgress(prev => ({
        ...prev,
        isImporting: false,
        errors: [...prev.errors, error.response?.data?.error || error.message]
      }));
    }
  };

  // Validate tenant database schema
  const handleValidateDatabase = async () => {
    setDbValidation({
      isValidating: true,
      isInitializing: false,
      isFixing: false,
      isAddingExtra: false,
      isDroppingExtra: false,
      status: null,
      results: {
        tablesChecked: 0,
        tablesValid: 0,
        tablesMissing: 0,
        tablesCreated: 0,
        tablesFailed: 0,
        columnsChecked: 0,
        columnsValid: 0,
        columnsMissing: 0,
        columnsMismatched: 0,
        columnsExtra: 0,
        extraColumns: {},
        columnsFixed: 0,
        columnsFailed: 0,
        columnsAddedToSchema: 0,
        columnsDropped: 0,
        seedDataInserted: false,
        errors: [],
        warnings: [],
        tableDetails: {},
        tableResults: {},
        tableErrors: [],
        fixErrors: []
      }
    });

    try {
      const response = await axios.post(`/api/tenants/${selectedTenant.code}/validate-database`);

      const result = response.data;
      
      setDbValidation({
        isValidating: false,
        isInitializing: false,
        isFixing: false,
        isAddingExtra: false,
        isDroppingExtra: false,
        status: result.status, // 'valid', 'invalid', 'missing'
        results: {
          tablesChecked: result.tablesChecked || 0,
          tablesValid: result.tablesValid || 0,
          tablesMissing: result.tablesMissing || 0,
          tablesCreated: 0,
          tablesFailed: 0,
          columnsChecked: result.columnsChecked || 0,
          columnsValid: result.columnsValid || 0,
          columnsMissing: result.columnsMissing || 0,
          columnsMismatched: result.columnsMismatched || 0,
          columnsExtra: result.columnsExtra || 0,
          extraColumns: result.extraColumns || {},
          columnsFixed: 0,
          columnsFailed: 0,
          columnsAddedToSchema: 0,
          columnsDropped: 0,
          seedDataInserted: false,
          errors: result.errors || [],
          warnings: result.warnings || [],
          tableDetails: result.tableDetails || {},
          tableResults: {},
          tableErrors: [],
          fixErrors: []
        }
      });

    } catch (error) {
      console.error('Error validating database:', error);
      setDbValidation(prev => ({
        ...prev,
        isValidating: false,
        status: 'error',
        results: {
          ...prev.results,
          errors: [...prev.results.errors, error.response?.data?.error || error.message]
        }
      }));
    }
  };

  // Fix missing columns in tenant database
  const handleFixMissingColumns = async () => {
    setDbValidation(prev => ({
      ...prev,
      isFixing: true
    }));

    try {
      const response = await axios.post(`/api/tenants/${selectedTenant.code}/fix-missing-columns`);

      const result = response.data;
      
      // Collect all errors from the response
      const allErrors = [];
      const fixErrors = [];
      
      // Add table creation errors
      if (result.tableErrors && Array.isArray(result.tableErrors)) {
        result.tableErrors.forEach(error => {
          if (typeof error === 'string') {
            allErrors.push(error);
          } else if (error.error) {
            allErrors.push(`${error.table || 'Unknown table'}: ${error.error}`);
          }
        });
      }
      
      // Add column fix errors
      if (result.errors && Array.isArray(result.errors)) {
        result.errors.forEach(error => {
          if (typeof error === 'string') {
            fixErrors.push(error);
            allErrors.push(error);
          } else if (error.column) {
            const errorMsg = `${error.column}: ${error.error || 'Unknown error'}`;
            fixErrors.push(errorMsg);
            allErrors.push(errorMsg);
          }
        });
      }
      
      // Extract errors from tableResults (both table creation and column fix errors)
      if (result.tableResults && typeof result.tableResults === 'object') {
        Object.entries(result.tableResults).forEach(([tableName, tableResult]) => {
          // Check for table-level errors (table creation failures)
          if (tableResult.status === 'error' || tableResult.status === 'failed') {
            const errorMsg = `Table ${tableName}: ${tableResult.error || 'Failed to create table'}`;
            allErrors.push(errorMsg);
            // Add to tableErrors if not already there
            if (!result.tableErrors || !result.tableErrors.some(e => 
              (typeof e === 'string' && e.includes(tableName)) ||
              (typeof e === 'object' && e.table === tableName)
            )) {
              if (!result.tableErrors) result.tableErrors = [];
              result.tableErrors.push({
                table: tableName,
                error: tableResult.error || 'Failed to create table'
              });
            }
          }
          
          // Check for column-level errors (column fix failures)
          if (tableResult.details && Array.isArray(tableResult.details)) {
            tableResult.details.forEach(detail => {
              if (detail.status === 'error' || detail.status === 'failed') {
                const errorMsg = `${tableName}.${detail.column}: ${detail.error || 'Failed to create'}`;
                fixErrors.push(errorMsg);
                allErrors.push(errorMsg);
              }
            });
          }
        });
      }
      
      setDbValidation(prev => ({
        ...prev,
        isFixing: false,
        results: {
          ...prev.results,
          tablesCreated: result.tablesCreated || 0,
          tablesFailed: result.tablesFailed || 0,
          tablesMissing: Math.max(0, (prev.results.tablesMissing || 0) - (result.tablesCreated || 0)),
          columnsFixed: result.columnsFixed || 0,
          columnsFailed: result.columnsFailed || 0,
          columnsMissing: Math.max(0, (prev.results.columnsMissing || 0) - (result.columnsFixed || 0)),
          tableResults: result.tableResults || {},
          tableErrors: result.tableErrors || [],
          fixErrors: fixErrors,
          errors: [...prev.results.errors, ...allErrors]
        }
      }));

      // Re-validate to get updated status (only if there were no critical errors)
      if (result.success !== false || (result.tablesCreated > 0 || result.columnsFixed > 0)) {
        await handleValidateDatabase();
      }

      // Build success/error message
      const successParts = [];
      const errorParts = [];
      
      if (result.tablesCreated > 0) {
        successParts.push(`Created ${result.tablesCreated} table(s)`);
      }
      if (result.tablesFailed > 0) {
        errorParts.push(`Failed to create ${result.tablesFailed} table(s)`);
      }
      if (result.columnsFixed > 0) {
        successParts.push(`Fixed ${result.columnsFixed} column(s)`);
      }
      if (result.columnsFailed > 0) {
        errorParts.push(`Failed to fix ${result.columnsFailed} column(s)`);
      }
      
      // Show error message if there are failures
      if (errorParts.length > 0 || allErrors.length > 0) {
        const errorDetails = errorParts.length > 0 
          ? `${errorParts.join(', ')}. ` 
          : '';
        const errorMsg = `⚠️ Schema fix completed with errors: ${errorDetails}Check the detailed error report below for specific issues.`;
        setError(errorMsg);
        setTimeout(() => setError(''), 15000); // Keep error visible longer
      }
      
      // Show success message if there were any successes
      if (successParts.length > 0) {
        const successMsg = `✅ Successfully ${successParts.join(' and ')}!`;
        setSuccessMessage(successMsg);
        setTimeout(() => setSuccessMessage(''), 5000);
      } else if (errorParts.length === 0 && allErrors.length === 0) {
        setSuccessMessage('No fixes were needed.');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else if (errorParts.length > 0 && successParts.length === 0) {
        // All operations failed
        setError('❌ All schema fix operations failed. Please check the error details below.');
        setTimeout(() => setError(''), 15000);
      }

    } catch (error) {
      console.error('Error fixing schema:', error);
      setDbValidation(prev => ({
        ...prev,
        isFixing: false,
        results: {
          ...prev.results,
          errors: [...prev.results.errors, error.response?.data?.error || error.message]
        }
      }));
      setError('Failed to fix schema: ' + (error.response?.data?.error || error.message));
      setTimeout(() => setError(''), 5000);
    }
  };

  // Add single extra column to schema configuration
  const handleAddSingleExtraColumn = async (tableName, columnName, columnDefinition) => {
    if (!window.confirm(`Add column "${columnName}" to schema configuration for table "${tableName}"?`)) {
      return;
    }

    setDbValidation(prev => ({
      ...prev,
      isAddingExtra: true
    }));

    try {
      const response = await axios.post(`/api/tenants/${selectedTenant.code}/add-extra-column`, {
        tableName,
        columnName,
        columnDefinition
      });

      const result = response.data;
      
      setDbValidation(prev => ({
        ...prev,
        isAddingExtra: false,
        results: {
          ...prev.results,
          columnsAddedToSchema: (prev.results.columnsAddedToSchema || 0) + 1,
          columnsExtra: Math.max(0, (prev.results.columnsExtra || 0) - 1),
          extraColumns: Object.fromEntries(
            Object.entries(prev.results.extraColumns || {}).map(([tName, cols]) => {
              if (tName === tableName) {
                const { [columnName]: removed, ...remaining } = cols;
                return [tName, remaining];
              }
              return [tName, cols];
            }).filter(([_, cols]) => Object.keys(cols).length > 0)
          )
        }
      }));

      setSuccessMessage(`Added column "${columnName}" to schema successfully!`);
      setTimeout(() => setSuccessMessage(''), 3000);

    } catch (error) {
      console.error('Error adding extra column:', error);
      setDbValidation(prev => ({
        ...prev,
        isAddingExtra: false,
        results: {
          ...prev.results,
          errors: [...prev.results.errors, error.response?.data?.error || error.message]
        }
      }));
      setError('Failed to add column to schema: ' + (error.response?.data?.error || error.message));
      setTimeout(() => setError(''), 5000);
    }
  };

  // Add all extra columns to schema configuration
  const handleAddAllExtraColumns = async () => {
    const extraCount = dbValidation.results.columnsExtra || 0;
    if (!window.confirm(`Add all ${extraCount} extra column(s) to schema configuration?`)) {
      return;
    }

    setDbValidation(prev => ({
      ...prev,
      isAddingExtra: true
    }));

    try {
      const response = await axios.post(`/api/tenants/${selectedTenant.code}/add-all-extra-columns`);

      const result = response.data;
      
      setDbValidation(prev => ({
        ...prev,
        isAddingExtra: false,
        results: {
          ...prev.results,
          columnsAddedToSchema: result.columnsAdded || 0,
          columnsExtra: 0,
          extraColumns: {}
        }
      }));

      setSuccessMessage(`Added ${result.columnsAdded} extra column(s) to schema successfully!`);
      setTimeout(() => setSuccessMessage(''), 3000);

    } catch (error) {
      console.error('Error adding extra columns:', error);
      setDbValidation(prev => ({
        ...prev,
        isAddingExtra: false,
        results: {
          ...prev.results,
          errors: [...prev.results.errors, error.response?.data?.error || error.message]
        }
      }));
      setError('Failed to add columns to schema: ' + (error.response?.data?.error || error.message));
      setTimeout(() => setError(''), 5000);
    }
  };

  // Drop single extra column from database
  const handleDropSingleExtraColumn = async (tableName, columnName) => {
    if (!window.confirm(`⚠️ WARNING: This will PERMANENTLY DELETE the column "${columnName}" and ALL ITS DATA from table "${tableName}"!\n\nThis action CANNOT be undone!\n\nAre you absolutely sure?`)) {
      return;
    }

    // Second confirmation for safety
    if (!window.confirm(`FINAL CONFIRMATION:\n\nYou are about to DROP column:\n"${tableName}.${columnName}"\n\nAll data in this column will be LOST FOREVER.\n\nType-to-confirm not available in browser, click OK to proceed or Cancel to abort.`)) {
      return;
    }

    setDbValidation(prev => ({
      ...prev,
      isDroppingExtra: true
    }));

    try {
      const response = await axios.post(`/api/tenants/${selectedTenant.code}/drop-extra-column`, {
        tableName,
        columnName
      });

      const result = response.data;
      
      setDbValidation(prev => ({
        ...prev,
        isDroppingExtra: false,
        results: {
          ...prev.results,
          columnsDropped: (prev.results.columnsDropped || 0) + 1,
          columnsExtra: Math.max(0, (prev.results.columnsExtra || 0) - 1),
          extraColumns: Object.fromEntries(
            Object.entries(prev.results.extraColumns || {}).map(([tName, cols]) => {
              if (tName === tableName) {
                const { [columnName]: removed, ...remaining } = cols;
                return [tName, remaining];
              }
              return [tName, cols];
            }).filter(([_, cols]) => Object.keys(cols).length > 0)
          )
        }
      }));

      setSuccessMessage(`Dropped column "${columnName}" from database successfully!`);
      setTimeout(() => setSuccessMessage(''), 3000);

    } catch (error) {
      console.error('Error dropping extra column:', error);
      setDbValidation(prev => ({
        ...prev,
        isDroppingExtra: false,
        results: {
          ...prev.results,
          errors: [...prev.results.errors, error.response?.data?.error || error.message]
        }
      }));
      setError('Failed to drop column from database: ' + (error.response?.data?.error || error.message));
      setTimeout(() => setError(''), 5000);
    }
  };

  // Drop all extra columns from database
  const handleDropAllExtraColumns = async () => {
    const extraCount = dbValidation.results.columnsExtra || 0;
    
    if (!window.confirm(`⚠️ DANGER: This will PERMANENTLY DELETE ${extraCount} column(s) and ALL THEIR DATA from the database!\n\nThis action CANNOT be undone!\n\nAre you absolutely sure?`)) {
      return;
    }

    // Second confirmation for safety
    if (!window.confirm(`FINAL CONFIRMATION:\n\nYou are about to DROP ${extraCount} column(s) from the database.\n\nAll data in these columns will be LOST FOREVER.\n\nClick OK to proceed or Cancel to abort.`)) {
      return;
    }

    setDbValidation(prev => ({
      ...prev,
      isDroppingExtra: true
    }));

    try {
      const response = await axios.post(`/api/tenants/${selectedTenant.code}/drop-all-extra-columns`);

      const result = response.data;
      
      setDbValidation(prev => ({
        ...prev,
        isDroppingExtra: false,
        results: {
          ...prev.results,
          columnsDropped: result.columnsDropped || 0,
          columnsExtra: 0,
          extraColumns: {}
        }
      }));

      setSuccessMessage(`Dropped ${result.columnsDropped} column(s) from database successfully!`);
      setTimeout(() => setSuccessMessage(''), 3000);

    } catch (error) {
      console.error('Error dropping extra columns:', error);
      setDbValidation(prev => ({
        ...prev,
        isDroppingExtra: false,
        results: {
          ...prev.results,
          errors: [...prev.results.errors, error.response?.data?.error || error.message]
        }
      }));
      setError('Failed to drop columns from database: ' + (error.response?.data?.error || error.message));
      setTimeout(() => setError(''), 5000);
    }
  };

  // Validate Unicode support in tenant database
  const handleValidateUnicode = async () => {
    setUnicodeValidation({
      isValidating: true,
      isFixing: false,
      status: null,
      results: {
        databaseCollation: '',
        columnDetails: {},
        unicodeTestPassed: false,
        corruptionFound: false,
        corruptedRecords: [],
        fixApplied: false,
        errors: [],
        warnings: []
      }
    });

    try {
      const response = await axios.post(`/api/tenants/${selectedTenant.code}/validate-unicode`);

      const result = response.data;
      
      setUnicodeValidation({
        isValidating: false,
        isFixing: false,
        status: result.status, // 'valid', 'invalid', 'error'
        results: {
          databaseCollation: result.databaseCollation || '',
          columnDetails: result.columnDetails || {},
          unicodeTestPassed: result.unicodeTestPassed || false,
          corruptionFound: result.corruptionFound || false,
          corruptedRecords: result.corruptedRecords || [],
          fixApplied: false,
          errors: result.errors || [],
          warnings: result.warnings || []
        }
      });

      if (result.status === 'valid') {
        setSuccessMessage('Unicode validation passed! Database supports Thai characters correctly.');
      } else if (result.status === 'invalid') {
        setError('Unicode validation failed! Database has issues with Thai character support.');
      }
      
      setTimeout(() => {
        setSuccessMessage('');
        setError('');
      }, 5000);

    } catch (error) {
      console.error('Error validating Unicode:', error);
      setUnicodeValidation(prev => ({
        ...prev,
        isValidating: false,
        status: 'error',
        results: {
          ...prev.results,
          errors: [...prev.results.errors, error.response?.data?.error || error.message]
        }
      }));
      setError(error.response?.data?.error || error.message);
      setTimeout(() => setError(''), 5000);
    }
  };

  // Fix Unicode schema issues in tenant database
  const handleFixUnicodeSchema = async () => {
    if (!window.confirm('⚠️ WARNING: This will modify the database schema to support Unicode characters properly.\n\nThis action will:\n- Backup existing data\n- Convert varchar columns to nvarchar for Unicode support\n- Test Unicode character insertion\n\nAre you sure you want to proceed?')) {
      return;
    }

    setUnicodeValidation(prev => ({
      ...prev,
      isFixing: true
    }));

    try {
      const response = await axios.post(`/api/tenants/${selectedTenant.code}/fix-unicode-schema`);

      const result = response.data;
      
      setUnicodeValidation(prev => ({
        ...prev,
        isFixing: false,
        status: result.status, // 'fixed', 'error'
        results: {
          ...prev.results,
          fixApplied: result.fixApplied || false,
          columnDetails: result.columnDetails || prev.results.columnDetails,
          unicodeTestPassed: result.unicodeTestPassed || false,
          errors: result.errors || [],
          warnings: result.warnings || []
        }
      }));

      if (result.status === 'fixed') {
        setSuccessMessage('Unicode schema fixed successfully! Database now supports Thai characters properly.');
      } else {
        setError('Failed to fix Unicode schema. Please check the logs for details.');
      }
      
      setTimeout(() => {
        setSuccessMessage('');
        setError('');
      }, 5000);

    } catch (error) {
      console.error('Error fixing Unicode schema:', error);
      setUnicodeValidation(prev => ({
        ...prev,
        isFixing: false,
        status: 'error',
        results: {
          ...prev.results,
          errors: [...prev.results.errors, error.response?.data?.error || error.message]
        }
      }));
      setError(error.response?.data?.error || error.message);
      setTimeout(() => setError(''), 5000);
    }
  };

  // Initialize tenant database with schema and seed data
  const handleInitializeDatabase = async () => {
    setDbValidation(prev => ({
      ...prev,
      isInitializing: true
    }));

    try {
      const response = await axios.post(`/api/tenants/${selectedTenant.code}/initialize-database`);

      const result = response.data;
      
      setDbValidation(prev => ({
        ...prev,
        isInitializing: false,
        status: 'valid',
        results: {
          ...prev.results,
          tablesCreated: result.tablesCreated || 0,
          seedDataInserted: result.seedDataInserted || false,
          errors: result.errors || [],
          warnings: result.warnings || []
        }
      }));

      // Update tenant database status
      setTenantDbStatus(prev => ({
        ...prev,
        [selectedTenant.code]: {
          status: 'valid',
          lastChecked: new Date().toISOString(),
          tablesValid: result.tablesValid || 0,
          tablesMissing: 0
        }
      }));

      setSuccessMessage('Database initialized successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);

    } catch (error) {
      console.error('Error initializing database:', error);
      setDbValidation(prev => ({
        ...prev,
        isInitializing: false,
        results: {
          ...prev.results,
          errors: [...prev.results.errors, error.response?.data?.error || error.message]
        }
      }));
    }
  };

  // Validate all tenant databases
  const handleValidateAllDatabases = async () => {
    setValidatingAll(true);
    const newStatus = {};

    try {
      for (const tenant of tenants) {
        try {
          console.log(`Validating database for tenant: ${tenant.code}`);
          
          const response = await axios.post(`/api/tenants/${tenant.code}/validate-database`);

          const result = response.data;
          newStatus[tenant.code] = {
            status: result.status,
            lastChecked: new Date().toISOString(),
            tablesValid: result.tablesValid || 0,
            tablesMissing: result.tablesMissing || 0,
            columnsChecked: result.columnsChecked || 0,
            columnsValid: result.columnsValid || 0,
            columnsMissing: result.columnsMissing || 0,
            columnsMismatched: result.columnsMismatched || 0,
            errors: result.errors || [],
            warnings: result.warnings || [],
            summary: result.summary || {}
          };
        } catch (error) {
          console.error(`Error validating tenant ${tenant.code}:`, error);
          newStatus[tenant.code] = {
            status: 'error',
            lastChecked: new Date().toISOString(),
            error: error.response?.data?.error || error.message
          };
        }
      }

      setTenantDbStatus(newStatus);
      setSuccessMessage('All tenant databases validated!');
      setTimeout(() => setSuccessMessage(''), 3000);

    } catch (error) {
      console.error('Error validating all databases:', error);
      setError('Failed to validate all databases');
      setTimeout(() => setError(''), 3000);
    } finally {
      setValidatingAll(false);
    }
  };

  // Validate single tenant database
  const handleValidateSingleDatabase = async (tenantCode) => {
    try {
      const response = await axios.post(`/api/tenants/${tenantCode}/validate-database`);

      const result = response.data;
      setTenantDbStatus(prev => ({
        ...prev,
        [tenantCode]: {
          status: result.status,
          lastChecked: new Date().toISOString(),
          tablesValid: result.tablesValid || 0,
          tablesMissing: result.tablesMissing || 0,
          columnsChecked: result.columnsChecked || 0,
          columnsValid: result.columnsValid || 0,
          columnsMissing: result.columnsMissing || 0,
          columnsMismatched: result.columnsMismatched || 0,
          errors: result.errors || [],
          warnings: result.warnings || [],
          summary: result.summary || {}
        }
      }));
    } catch (error) {
      console.error(`Error validating tenant ${tenantCode}:`, error);
      setTenantDbStatus(prev => ({
        ...prev,
        [tenantCode]: {
          status: 'error',
          lastChecked: new Date().toISOString(),
          error: error.response?.data?.error || error.message
        }
      }));
    }
  };

  // View detailed validation report
  const handleViewValidationReport = async (tenantCode) => {
    try {
      const response = await axios.get(`/api/tenants/${tenantCode}/validation-report`);
      const report = response.data.report;
      
      // Create a detailed report display
      const reportWindow = window.open('', '_blank', 'width=1000,height=800');
      reportWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Database Validation Report - ${tenantCode}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; background: #1f2937; color: #f9fafb; }
            .header { background: #374151; padding: 20px; border-radius: 8px; margin-bottom: 20px; }
            .summary { background: #4b5563; padding: 15px; border-radius: 8px; margin-bottom: 20px; }
            .table-section { background: #374151; padding: 15px; border-radius: 8px; margin-bottom: 15px; }
            .table-name { font-weight: bold; color: #60a5fa; margin-bottom: 10px; }
            .column-item { margin: 5px 0; padding: 5px; background: #1f2937; border-radius: 4px; }
            .valid { color: #10b981; }
            .missing { color: #ef4444; }
            .mismatched { color: #f59e0b; }
            .status-badge { padding: 4px 8px; border-radius: 4px; font-size: 12px; font-weight: bold; }
            .status-valid { background: #10b981; color: white; }
            .status-invalid { background: #ef4444; color: white; }
            .status-missing { background: #f59e0b; color: white; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Database Validation Report</h1>
            <p><strong>Tenant:</strong> ${report.tenantName} (${report.tenantCode})</p>
            <p><strong>Validated At:</strong> ${new Date(report.validatedAt).toLocaleString()}</p>
            <p><strong>Overall Status:</strong> <span class="status-badge status-${report.overallStatus}">${report.overallStatus.toUpperCase()}</span></p>
          </div>
          
          <div class="summary">
            <h2>Summary</h2>
            <p><strong>Total Tables:</strong> ${report.summary.totalTables}</p>
            <p><strong>Valid Tables:</strong> ${report.summary.validTables}</p>
            <p><strong>Total Columns:</strong> ${report.summary.totalColumns}</p>
            <p><strong>Valid Columns:</strong> ${report.summary.validColumns}</p>
          </div>
          
          <h2>Table Details</h2>
          ${Object.entries(report.tableSummary).map(([tableName, tableInfo]) => `
            <div class="table-section">
              <div class="table-name">${tableName}</div>
              <p><strong>Status:</strong> <span class="status-badge status-${tableInfo.status}">${tableInfo.status.toUpperCase()}</span></p>
              <p><strong>Columns Valid:</strong> ${tableInfo.columnsValid}</p>
              <p><strong>Columns Missing:</strong> ${tableInfo.columnsMissing}</p>
              <p><strong>Columns Mismatched:</strong> ${tableInfo.columnsMismatched}</p>
              ${tableInfo.issues.length > 0 ? `
                <h4>Issues:</h4>
                <ul>
                  ${tableInfo.issues.map(issue => `<li>${issue}</li>`).join('')}
                </ul>
              ` : ''}
            </div>
          `).join('')}
          
          ${report.issues.critical.length > 0 ? `
            <div class="table-section">
              <h2 style="color: #ef4444;">Critical Issues</h2>
              <ul>
                ${report.issues.critical.map(issue => `<li class="missing">${issue}</li>`).join('')}
              </ul>
            </div>
          ` : ''}
          
          ${report.issues.warnings.length > 0 ? `
            <div class="table-section">
              <h2 style="color: #f59e0b;">Warnings</h2>
              <ul>
                ${report.issues.warnings.map(issue => `<li class="mismatched">${issue}</li>`).join('')}
              </ul>
            </div>
          ` : ''}
        </body>
        </html>
      `);
      reportWindow.document.close();
      
    } catch (error) {
      console.error(`Error fetching validation report for tenant ${tenantCode}:`, error);
      setError(`Failed to generate validation report: ${error.response?.data?.error || error.message}`);
    }
  };

  if (loading && tenants.length === 0) {
    return (
      <div className="loading-container bg-gray-900 min-h-screen flex items-center justify-center">
        <div className="text-center bg-gray-800/50 p-8 rounded-2xl border-2 border-gray-700/50 shadow-2xl">
          <div className="loading-spinner w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-100 text-lg font-semibold">Loading tenants...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="tenant-management bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className="management-header bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Tenant Management</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Manage tenant configurations and database schemas</p>
          </div>
          <div className="flex space-x-3">
            <button 
              className="validate-all-btn bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 px-4 py-2 rounded-lg flex items-center space-x-2 transition-all duration-200 disabled:opacity-50 font-medium shadow-sm hover:shadow"
              onClick={handleValidateAllDatabases}
              disabled={validatingAll}
            >
              {validatingAll ? (
                <>
                  <div className="w-4 h-4 border-2 border-gray-600 dark:border-gray-300 border-t-transparent rounded-full animate-spin"></div>
                  <span>Validating...</span>
                </>
              ) : (
                <>
                  <span>🔍</span>
                  <span>Validate All</span>
                </>
              )}
            </button>
            <button 
              className="test-email-domain-btn bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 px-4 py-2 rounded-lg flex items-center space-x-2 transition-all duration-200 font-medium shadow-sm hover:shadow"
              onClick={() => {
                const modal = document.querySelector('.modal-overlay[style*="display: none"]');
                if (modal) {
                  modal.style.display = 'flex';
                }
              }}
            >
              <span>📧</span>
              <span>Test Email</span>
            </button>
            <button 
              className="create-btn bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-all duration-200 font-medium shadow-sm hover:shadow"
              onClick={() => setActiveModal('create')}
            >
              <span>➕</span>
              <span>Create Tenant</span>
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="error-message bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200 p-4 mx-6 mt-6 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="font-medium">{error}</span>
            <button onClick={() => setError('')} className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 text-xl font-bold transition-colors">×</button>
          </div>
          {pendingRestoreTenant && (
            <div className="mt-3 flex space-x-3">
              <button
                onClick={handleRestoreTenant}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200"
                disabled={loading}
              >
                {loading ? 'Restoring...' : 'Restore Tenant'}
              </button>
              <button
                onClick={() => {
                  setPendingRestoreTenant(null);
                  setError('');
                }}
                className="bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200"
              >
                Use Different Code
              </button>
            </div>
          )}
        </div>
      )}

      {successMessage && (
        <div className="success-message bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-200 p-4 mx-6 mt-6 rounded-lg flex items-center justify-between">
          <span className="font-medium">{successMessage}</span>
          <button onClick={() => setSuccessMessage('')} className="text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-200 text-xl font-bold transition-colors">×</button>
        </div>
      )}

             <div className="tenants-grid p-6 max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
         {tenants.map((tenant) => (
           <div key={tenant.id} className="tenant-card bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm hover:shadow-xl transition-all duration-300" style={{ position: 'relative', overflow: 'visible', zIndex: expandedMenu[tenant.code] ? 9999 : 'auto' }}>
            {/* Header Section */}
            <div className="tenant-card-header bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-700 dark:to-gray-800 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-3 flex-1 min-w-0">
                  {tenant.companyLogo && (
                    <img 
                      src={tenant.companyLogo} 
                      alt={`${tenant.companyName || tenant.name} logo`}
                      className="tenant-logo w-14 h-14 rounded-xl object-cover border-2 border-white dark:border-gray-600 shadow-sm flex-shrink-0"
                    />
                  )}
                  <div className="tenant-title flex-1 min-w-0">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white truncate mb-1">
                      {tenant.companyName || tenant.name}
                    </h3>
                    <div className="flex items-center space-x-2">
                      <span className="tenant-code inline-block text-xs text-blue-700 dark:text-blue-300 font-semibold bg-blue-100 dark:bg-blue-900/50 px-2.5 py-1 rounded-md">
                        {tenant.code}
                      </span>
                      {tenant.isDefault && (
                        <span className="inline-block text-xs text-yellow-700 dark:text-yellow-300 font-semibold bg-yellow-100 dark:bg-yellow-900/50 px-2.5 py-1 rounded-md">
                          Default
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Content Section */}
            <div className="tenant-info px-6 py-5">
              {/* Description */}
              {tenant.description && (
                <p className="tenant-description text-gray-600 dark:text-gray-400 mb-5 text-sm leading-relaxed line-clamp-2">
                  {tenant.description}
                </p>
              )}
              
              {/* Stats Grid */}
              <div className="tenant-stats grid grid-cols-2 gap-3 mb-5">
                {/* Access Level */}
                <div className="stat-item bg-gray-50 dark:bg-gray-700/30 rounded-lg p-3">
                  <div className="text-xs text-gray-500 dark:text-gray-400 font-medium mb-1.5">Access</div>
                  <span className={`access-badge inline-block px-2.5 py-1 rounded-md text-xs font-semibold ${tenant.accessLevel.toLowerCase() === 'admin' ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300' : tenant.accessLevel.toLowerCase() === 'write' ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300' : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}>
                    {tenant.accessLevel}
                  </span>
                </div>

                {/* Package */}
                <div className="stat-item bg-gray-50 dark:bg-gray-700/30 rounded-lg p-3">
                  <div className="text-xs text-gray-500 dark:text-gray-400 font-medium mb-1.5">Package</div>
                  {tenantPackages[tenant.code] ? (
                    <span className="package-badge inline-block bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 px-2.5 py-1 rounded-md text-xs font-semibold truncate max-w-full">
                      {tenantPackages[tenant.code].package?.packageName || 'Assigned'}
                    </span>
                  ) : (
                    <span className="text-gray-400 dark:text-gray-500 text-xs">None</span>
                  )}
                </div>

                {/* Plan */}
                {tenant.subscription && (
                  <div className="stat-item bg-gray-50 dark:bg-gray-700/30 rounded-lg p-3">
                    <div className="text-xs text-gray-500 dark:text-gray-400 font-medium mb-1.5">Plan</div>
                    <span className="subscription-badge inline-block bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 px-2.5 py-1 rounded-md text-xs font-semibold">
                      {tenant.subscription.plan}
                    </span>
                  </div>
                )}

                {/* Database Status */}
                <div className="stat-item bg-gray-50 dark:bg-gray-700/30 rounded-lg p-3">
                  <div className="text-xs text-gray-500 dark:text-gray-400 font-medium mb-1.5">Database</div>
                  {tenantDbStatus[tenant.code] ? (
                    <span className={`db-status-badge inline-block px-2.5 py-1 rounded-md text-xs font-semibold ${
                      tenantDbStatus[tenant.code].status === 'valid' ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300' :
                      tenantDbStatus[tenant.code].status === 'invalid' ? 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300' :
                      tenantDbStatus[tenant.code].status === 'missing' ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300' :
                      'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}>
                      {tenantDbStatus[tenant.code].status === 'valid' ? '✓ Valid' :
                       tenantDbStatus[tenant.code].status === 'invalid' ? '⚠ Invalid' :
                       tenantDbStatus[tenant.code].status === 'missing' ? '✗ Missing' :
                       '? Unknown'}
                    </span>
                  ) : (
                    <button 
                      className="check-db-btn bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 px-2.5 py-1 rounded-md text-xs font-medium transition-all duration-200"
                      onClick={() => handleValidateSingleDatabase(tenant.code)}
                    >
                      Check
                    </button>
                  )}
                </div>
              </div>

              {/* Additional Info Section */}
              {(tenant.adminEmail || (tenant.emailDomains && tenant.emailDomains.length > 0)) && (
                <div className="additional-info space-y-3 mb-5 pt-4 border-t border-gray-200 dark:border-gray-700">
                  {tenant.adminEmail && (
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-gray-500 dark:text-gray-400 font-medium min-w-[60px]">Admin:</span>
                      <span className="text-sm text-gray-700 dark:text-gray-300 truncate">{tenant.adminEmail}</span>
                    </div>
                  )}
                  {tenant.emailDomains && tenant.emailDomains.length > 0 && (
                    <div className="flex items-start space-x-2">
                      <span className="text-xs text-gray-500 dark:text-gray-400 font-medium min-w-[60px] pt-0.5">Domains:</span>
                      <div className="flex flex-wrap gap-1.5 flex-1">
                        {tenant.emailDomains.slice(0, 2).map((domain, index) => (
                          <span 
                            key={index}
                            className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"
                          >
                            @{domain}
                          </span>
                        ))}
                        {tenant.emailDomains.length > 2 && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            +{tenant.emailDomains.length - 2}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Actions Section */}
            <div className="tenant-actions bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700 px-6 py-4">
              <div className="flex items-center justify-between">
                {/* Primary Actions */}
                <div className="flex items-center space-x-2">
                  <button 
                    className="view-details-btn bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 flex items-center space-x-2 shadow-sm hover:shadow-md"
                    onClick={() => {
                      setSelectedTenant(tenant);
                      setActiveModal('details');
                    }}
                    title="View Details"
                  >
                    <Eye className="h-4 w-4" />
                    <span>View</span>
                  </button>
                  <button 
                    className="edit-tenant-btn bg-gray-600 dark:bg-gray-700 hover:bg-gray-700 dark:hover:bg-gray-600 text-white px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 flex items-center space-x-2 shadow-sm hover:shadow-md"
                    onClick={() => openEditModal(tenant)}
                    title="Edit Tenant"
                  >
                    <Edit className="h-4 w-4" />
                    <span>Edit</span>
                  </button>
                </div>

                {/* Actions Menu */}
                <div className="relative" style={{ zIndex: expandedMenu[tenant.code] ? 10000 : 50 }}>
                  <button
                    className="actions-menu-btn bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 p-2.5 rounded-lg transition-all duration-200 flex items-center justify-center shadow-sm hover:shadow-md"
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpandedMenu(prev => ({
                        ...prev,
                        [tenant.code]: !prev[tenant.code]
                      }));
                    }}
                    title="More Actions"
                  >
                    <MoreVertical className="h-5 w-5" />
                  </button>

                  {/* Dropdown Menu */}
                  {expandedMenu[tenant.code] && (
                    <>
                      <div 
                        className="fixed inset-0 z-[100]" 
                        onClick={() => setExpandedMenu(prev => ({
                          ...prev,
                          [tenant.code]: false
                        }))}
                      />
                      <div 
                        className="dropdown-menu-container absolute right-0 top-full mt-2 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-700 py-1"
                        style={{ 
                          zIndex: 10001,
                          maxHeight: 'calc(100vh - 200px)',
                          overflowY: 'auto'
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        onMouseEnter={(e) => {
                          // Keep menu open when hovering over dropdown
                          e.stopPropagation();
                        }}
                      >
                        {/* Configuration Section */}
                        <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Configuration
                        </div>
                        <button
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedTenantForAiConfig(tenant);
                            setShowAiConfigModal(true);
                            setExpandedMenu(prev => ({ ...prev, [tenant.code]: false }));
                          }}
                        >
                          <Brain className="h-4 w-4 text-purple-600" />
                          <span>AI Configuration</span>
                        </button>
                        <button
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenPackageModal(tenant);
                            setExpandedMenu(prev => ({ ...prev, [tenant.code]: false }));
                          }}
                        >
                          <PackageIcon className="h-4 w-4 text-indigo-600" />
                          <span>{tenantPackages[tenant.code] ? 'Change Package' : 'Assign Package'}</span>
                        </button>

                        {/* User Management Section */}
                        <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
                        <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          User Management
                        </div>
                        <button
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedTenant(tenant);
                            setActiveModal('assign');
                            setExpandedMenu(prev => ({ ...prev, [tenant.code]: false }));
                          }}
                        >
                          <UserPlus className="h-4 w-4 text-green-600" />
                          <span>Assign User</span>
                        </button>
                        <button
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedTenant(tenant);
                            setActiveModal('import');
                            setExpandedMenu(prev => ({ ...prev, [tenant.code]: false }));
                          }}
                        >
                          <Download className="h-4 w-4 text-amber-600" />
                          <span>Import Users</span>
                        </button>

                        {/* Database Section */}
                        <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
                        <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Database
                        </div>
                        <button
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedTenant(tenant);
                            setActiveModal('validate-db');
                            setExpandedMenu(prev => ({ ...prev, [tenant.code]: false }));
                          }}
                        >
                          <CheckCircle className="h-4 w-4 text-blue-600" />
                          <span>Validate Database</span>
                        </button>
                        <button
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedTenant(tenant);
                            setActiveModal('validate-unicode');
                            setExpandedMenu(prev => ({ ...prev, [tenant.code]: false }));
                          }}
                        >
                          <FileText className="h-4 w-4 text-purple-600" />
                          <span>Unicode Validation</span>
                        </button>
                        <button
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewValidationReport(tenant.code);
                            setExpandedMenu(prev => ({ ...prev, [tenant.code]: false }));
                          }}
                        >
                          <FileText className="h-4 w-4 text-cyan-600" />
                          <span>View Report</span>
                        </button>

                        {/* Danger Zone */}
                        <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
                        <button
                          className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center space-x-2 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedTenant(tenant);
                            setActiveModal('delete');
                            setExpandedMenu(prev => ({ ...prev, [tenant.code]: false }));
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                          <span>Delete Tenant</span>
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Create Tenant Modal */}
      {activeModal === 'create' && (
        <div className="modal-overlay fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="modal large bg-gray-900 border border-gray-600 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="modal-header bg-gray-800 border-b border-gray-600 p-6 flex items-center justify-between rounded-t-xl">
              <h3 className="text-xl font-semibold text-white">Create New Tenant</h3>
              <button 
                className="close-btn text-gray-400 hover:text-white p-2 hover:bg-gray-700 rounded-lg transition-all duration-200 hover:scale-105"
                onClick={() => setActiveModal(null)}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleCreateTenant} className="modal-form p-6 space-y-6 bg-gray-900 text-white">
              {/* Basic Information Section */}
              <div className="form-section bg-gray-800 border border-gray-600 rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-200">
                <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mr-3"></span>
                  Basic Information
                </h4>
                <div className="form-row grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="form-group">
                    <label className="block text-sm font-medium text-white mb-2">Tenant Code *</label>
                    <input
                      type="text"
                      value={newTenant.tenantCode}
                      onChange={(e) => setNewTenant({
                        ...newTenant,
                        tenantCode: e.target.value.toUpperCase()
                      })}
                      placeholder="e.g., CLIENT1, BRANCH_A"
                      required
                      className="w-full px-4 py-3 border border-gray-500 rounded-lg bg-gray-800 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-gray-400"
                    />
                    <small className="text-gray-300 text-xs">Uppercase letters, numbers, underscores, and hyphens only</small>
                  </div>

                  <div className="form-group">
                    <label className="block text-sm font-medium text-white mb-2">Tenant Name *</label>
                    <input
                      type="text"
                      value={newTenant.tenantName}
                      onChange={(e) => setNewTenant({
                        ...newTenant,
                        tenantName: e.target.value
                      })}
                      placeholder="e.g., Client ABC Corporation"
                      required
                      className="w-full px-4 py-3 border border-gray-500 rounded-lg bg-gray-800 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-gray-400"
                    />
                  </div>
                </div>

                <div className="form-group mt-4">
                  <label className="block text-sm font-medium text-white mb-2">Description</label>
                  <textarea
                    value={newTenant.description}
                    onChange={(e) => setNewTenant({
                      ...newTenant,
                      description: e.target.value
                    })}
                    placeholder="Brief description of this tenant"
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-500 rounded-lg bg-gray-800 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              {/* Company Information Section */}
              <div className="form-section bg-gray-800 border border-gray-600 rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-200">
                <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
                  <span className="w-2 h-2 bg-green-500 rounded-full mr-3"></span>
                  Company Information
                </h4>
                <div className="form-row grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="form-group">
                    <label className="block text-sm font-medium text-white mb-2">Company Name *</label>
                    <input
                      type="text"
                      value={newTenant.companyName}
                      onChange={(e) => setNewTenant({
                        ...newTenant,
                        companyName: e.target.value
                      })}
                      placeholder="e.g., ABC Corporation"
                      required
                      className="w-full px-4 py-3 border border-gray-500 rounded-lg bg-gray-800 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-gray-400"
                    />
                  </div>

                  <div className="form-group">
                    <label className="block text-sm font-medium text-white mb-2">Company Logo URL</label>
                    <input
                      type="url"
                      value={newTenant.companyLogo}
                      onChange={(e) => setNewTenant({
                        ...newTenant,
                        companyLogo: e.target.value
                      })}
                      placeholder="https://example.com/logo.png"
                      className="w-full px-4 py-3 border border-gray-500 rounded-lg bg-gray-800 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-gray-400"
                    />
                  </div>
                </div>

                <div className="form-row grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div className="form-group">
                    <label className="block text-sm font-medium text-white mb-2">Admin Email *</label>
                    <input
                      type="email"
                      value={newTenant.adminEmail}
                      onChange={(e) => setNewTenant({
                        ...newTenant,
                        adminEmail: e.target.value
                      })}
                      placeholder="admin@company.com"
                      required
                      className="w-full px-4 py-3 border border-gray-500 rounded-lg bg-gray-800 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-gray-400"
                    />
                  </div>

                  <div className="form-group">
                    <label className="block text-sm font-medium text-white mb-2">Primary Contact</label>
                    <input
                      type="text"
                      value={newTenant.primaryContact}
                      onChange={(e) => setNewTenant({
                        ...newTenant,
                        primaryContact: e.target.value
                      })}
                      placeholder="John Doe"
                      className="w-full px-4 py-3 border border-gray-500 rounded-lg bg-gray-800 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-gray-400"
                    />
                  </div>
                </div>

                <div className="form-row grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div className="form-group">
                    <label className="block text-sm font-medium text-white mb-2">Phone Number</label>
                    <input
                      type="tel"
                      value={newTenant.phoneNumber}
                      onChange={(e) => setNewTenant({
                        ...newTenant,
                        phoneNumber: e.target.value
                      })}
                      placeholder="+1 (555) 123-4567"
                      className="w-full px-4 py-3 border border-gray-500 rounded-lg bg-gray-800 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-gray-400"
                    />
                  </div>

                  <div className="form-group">
                    <label className="block text-sm font-medium text-white mb-2">Website</label>
                    <input
                      type="url"
                      value={newTenant.website}
                      onChange={(e) => setNewTenant({
                        ...newTenant,
                        website: e.target.value
                      })}
                      placeholder="https://company.com"
                      className="w-full px-4 py-3 border border-gray-500 rounded-lg bg-gray-800 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-gray-400"
                    />
                  </div>
                </div>

                <div className="form-group mt-4">
                  <label className="block text-sm font-medium text-white mb-2">Address</label>
                  <textarea
                    value={newTenant.address}
                    onChange={(e) => setNewTenant({
                      ...newTenant,
                      address: e.target.value
                    })}
                    placeholder="Company address"
                    rows="2"
                    className="w-full px-3 py-2 border border-gray-500 rounded-lg bg-gray-800 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

                             {/* Technical Configuration Section */}
               <div className="form-section bg-gray-800 border border-gray-600 rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-200">
                 <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
                   <span className="w-2 h-2 bg-purple-500 rounded-full mr-3"></span>
                   Technical Configuration
                 </h4>

                 {/* Database Type Selection */}
                 <div className="form-group mb-4">
                   <label className="block text-sm font-medium text-white mb-2">Database Type *</label>
                   <div className="database-type-selector flex space-x-4">
                     {tenantConfig.databaseTypes.map((dbType) => (
                       <label key={dbType.value} className="flex items-center space-x-2 text-white hover:text-gray-100 cursor-pointer">
                         <input
                           type="radio"
                           name="databaseType"
                           value={dbType.value}
                           checked={newTenant.databaseType === dbType.value}
                           onChange={(e) => {
                             const selectedDbType = tenantConfig.databaseTypes.find(dt => dt.value === e.target.value);
                             setNewTenant({
                               ...newTenant,
                               databaseType: e.target.value,
                               // Reset database-specific fields when switching types
                               databaseServer: 'localhost',
                               databaseUsername: selectedDbType?.defaultUsername || 'sa',
                               databasePassword: '',
                               databasePort: selectedDbType?.defaultPort?.toString() || '1433'
                             });
                           }}
                           className="w-4 h-4 text-blue-600 bg-gray-800 border-gray-400 rounded focus:ring-blue-500 focus:ring-2 hover:bg-gray-700 transition-all duration-200"
                         />
                         <span className="flex items-center space-x-2">
                           <span className={`w-6 h-6 ${dbType.value === 'sqlserver' ? 'bg-blue-600' : 'bg-green-600'} rounded flex items-center justify-center text-white text-xs font-bold`}>
                             {dbType.value === 'sqlserver' ? 'SQL' : 'PG'}
                           </span>
                           <span>{dbType.label}</span>
                         </span>
                       </label>
                     ))}
                   </div>
                   <small className="text-gray-300 text-xs mt-1">
                     Select the database type for this tenant
                   </small>
                 </div>

                 <div className="form-row grid grid-cols-1 md:grid-cols-2 gap-4">
                   <div className="form-group">
                     <label className="block text-sm font-medium text-white mb-2">Database Name *</label>
                     <input
                       type="text"
                       value={newTenant.databaseName}
                       onChange={(e) => setNewTenant({
                         ...newTenant,
                         databaseName: e.target.value
                       })}
                       placeholder={newTenant.databaseType === 'postgresql' ? 'e.g., crm_client1_db' : 'e.g., CRM_CLIENT1_DB'}
                       required
                       className="w-full px-4 py-3 border border-gray-500 rounded-lg bg-gray-800 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-gray-400"
                     />
                   </div>

                   <div className="form-group">
                     <label className="block text-sm font-medium text-white mb-2">Upload Directory *</label>
                     <input
                       type="text"
                       value={newTenant.uploadDirectory}
                       onChange={(e) => setNewTenant({
                         ...newTenant,
                         uploadDirectory: e.target.value
                       })}
                       placeholder="e.g., uploads/client1"
                       required
                       className="w-full px-4 py-3 border border-gray-500 rounded-lg bg-gray-800 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-gray-400"
                     />
                   </div>
                 </div>

                 {/* Database-specific configuration */}
                 {newTenant.databaseType === 'postgresql' && (
                   <>
                     <div className="form-row grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                       <div className="form-group">
                         <label className="block text-sm font-medium text-white mb-2">Database Username *</label>
                         <input
                           type="text"
                           value={newTenant.databaseUsername}
                           onChange={(e) => setNewTenant({
                             ...newTenant,
                             databaseUsername: e.target.value
                           })}
                           placeholder="e.g., postgres"
                           required
                           className="w-full px-4 py-3 border border-gray-500 rounded-lg bg-gray-800 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-gray-400"
                         />
                       </div>

                       <div className="form-group">
                         <label className="block text-sm font-medium text-white mb-2">Database Password *</label>
                         <input
                           type="password"
                           value={newTenant.databasePassword}
                           onChange={(e) => setNewTenant({
                             ...newTenant,
                             databasePassword: e.target.value
                           })}
                           placeholder="Enter PostgreSQL password"
                           required
                           className="w-full px-4 py-3 border border-gray-500 rounded-lg bg-gray-800 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-gray-400"
                         />
                       </div>
                     </div>
                     <small className="text-gray-300 text-xs mt-2 block">
                       PostgreSQL superuser credentials required for database creation and setup
                     </small>
                   </>
                 )}

                 <div className="form-row grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                   <div className="form-group">
                     <label className="block text-sm font-medium text-white mb-2">Database Server</label>
                     <input
                       type="text"
                       value={newTenant.databaseServer}
                       onChange={(e) => setNewTenant({
                         ...newTenant,
                         databaseServer: e.target.value
                       })}
                       placeholder={newTenant.databaseType === 'postgresql' ? 'localhost:5432' : 'localhost'}
                       className="w-full px-4 py-3 border border-gray-500 rounded-lg bg-gray-800 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-gray-400"
                     />
                     <small className="text-gray-300 text-xs mt-1">
                       {newTenant.databaseType === 'postgresql'
                         ? 'PostgreSQL server host and port (default: localhost:5432)'
                         : 'SQL Server host (default: localhost)'}
                     </small>
                   </div>

                   <div className="form-group">
                     <label className="block text-sm font-medium text-white mb-2">Port</label>
                     <input
                       type="number"
                       value={newTenant.databasePort || (newTenant.databaseType === 'postgresql' ? '5432' : '1433')}
                       onChange={(e) => setNewTenant({
                         ...newTenant,
                         databasePort: e.target.value
                       })}
                       placeholder={newTenant.databaseType === 'postgresql' ? '5432' : '1433'}
                       min="1"
                       max="65535"
                       className="w-full px-4 py-3 border border-gray-500 rounded-lg bg-gray-800 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-gray-400"
                     />
                     <small className="text-gray-300 text-xs mt-1">
                       {newTenant.databaseType === 'postgresql'
                         ? 'PostgreSQL port (default: 5432)'
                         : 'SQL Server port (default: 1433)'}
                     </small>
                   </div>
                 </div>

                 <div className="form-row grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div className="form-group">
                    <label className="block text-sm font-medium text-white mb-2">Timezone</label>
                    <select
                      value={newTenant.timezone}
                      onChange={(e) => setNewTenant({
                        ...newTenant,
                        timezone: e.target.value
                      })}
                      className="w-full px-4 py-3 border border-gray-500 rounded-lg bg-gray-800 text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-gray-400"
                    >
                      {tenantConfig.timezones.map((tz) => (
                        <option key={tz.value} value={tz.value}>
                          {tz.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="block text-sm font-medium text-white mb-2">Currency</label>
                    <select
                      value={newTenant.currency}
                      onChange={(e) => setNewTenant({
                        ...newTenant,
                        currency: e.target.value
                      })}
                      className="w-full px-4 py-3 border border-gray-500 rounded-lg bg-gray-800 text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-gray-400"
                    >
                      {tenantConfig.currencies.map((curr) => (
                        <option key={curr.value} value={curr.value}>
                          {curr.label}
                        </option>
                      ))}
                    </select>
                  </div>
                 </div>

                <div className="form-group mt-4">
                  <label className="block text-sm font-medium text-white mb-2">Language</label>
                  <select
                    value={newTenant.language}
                    onChange={(e) => setNewTenant({
                      ...newTenant,
                      language: e.target.value
                    })}
                    className="w-full px-3 py-2 border border-gray-500 rounded-lg bg-gray-800 text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {tenantConfig.languages.map((lang) => (
                      <option key={lang.value} value={lang.value}>
                        {lang.label}
                      </option>
                    ))}
                  </select>
                </div>
               </div>

               {/* Brand Colors Section */}
               <div className="form-section bg-gray-800 border border-gray-600 rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-200">
                 <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
                   <span className="w-2 h-2 bg-pink-500 rounded-full mr-3"></span>
                   Brand Colors
                 </h4>
                 <div className="form-row grid grid-cols-1 md:grid-cols-3 gap-4">
                   <div className="form-group">
                     <label className="block text-sm font-medium text-white mb-2">Primary Color</label>
                     <div className="color-input-group flex space-x-2">
                       <input
                         type="color"
                         value={newTenant.brandColors.primary}
                         onChange={(e) => setNewTenant({
                           ...newTenant,
                           brandColors: {
                             ...newTenant.brandColors,
                             primary: e.target.value
                           }
                         })}
                         className="w-12 h-10 border border-gray-500 rounded-lg bg-gray-800 hover:border-gray-300 transition-all duration-200 cursor-pointer"
                       />
                       <input
                         type="text"
                         value={newTenant.brandColors.primary}
                         onChange={(e) => setNewTenant({
                           ...newTenant,
                           brandColors: {
                             ...newTenant.brandColors,
                             primary: e.target.value
                           }
                         })}
                         placeholder="#007bff"
                         className="flex-1 px-3 py-2 border border-gray-500 rounded-lg bg-gray-800 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                       />
                     </div>
                   </div>

                   <div className="form-group">
                     <label className="block text-sm font-medium text-white mb-2">Secondary Color</label>
                     <div className="color-input-group flex space-x-2">
                       <input
                         type="color"
                         value={newTenant.brandColors.secondary}
                         onChange={(e) => setNewTenant({
                           ...newTenant,
                           brandColors: {
                             ...newTenant.brandColors,
                             secondary: e.target.value
                           }
                         })}
                         className="w-12 h-10 border border-gray-500 rounded-lg bg-gray-800 hover:border-gray-300 transition-all duration-200 cursor-pointer"
                       />
                       <input
                         type="text"
                         value={newTenant.brandColors.secondary}
                         onChange={(e) => setNewTenant({
                           ...newTenant,
                           brandColors: {
                             ...newTenant.brandColors,
                             secondary: e.target.value
                           }
                         })}
                         placeholder="#6c757d"
                         className="flex-1 px-3 py-2 border border-gray-500 rounded-lg bg-gray-800 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                       />
                     </div>
                   </div>

                   <div className="form-group">
                     <label className="block text-sm font-medium text-white mb-2">Accent Color</label>
                     <div className="color-input-group flex space-x-2">
                       <input
                         type="color"
                         value={newTenant.brandColors.accent}
                         onChange={(e) => setNewTenant({
                           ...newTenant,
                           brandColors: {
                             ...newTenant.brandColors,
                             accent: e.target.value
                           }
                         })}
                         className="w-12 h-10 border border-gray-500 rounded-lg bg-gray-800 hover:border-gray-300 transition-all duration-200 cursor-pointer"
                       />
                       <input
                         type="text"
                         value={newTenant.brandColors.accent}
                         onChange={(e) => setNewTenant({
                           ...newTenant,
                           brandColors: {
                             ...newTenant.brandColors,
                             accent: e.target.value
                           }
                         })}
                         placeholder="#28a745"
                         className="flex-1 px-3 py-2 border border-gray-500 rounded-lg bg-gray-800 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                       />
                     </div>
                   </div>
                 </div>
               </div>

               {/* Features Section */}
               <div className="form-section bg-gray-800 border border-gray-600 rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-200">
                 <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
                   <span className="w-2 h-2 bg-yellow-500 rounded-full mr-3"></span>
                   Features
                 </h4>
                 <div className="checkbox-grid grid grid-cols-1 md:grid-cols-2 gap-4">
                   <label className="checkbox-item flex items-center space-x-3 text-white hover:text-gray-100 cursor-pointer p-2 rounded-lg hover:bg-gray-700 transition-all duration-200">
                     <input
                       type="checkbox"
                       checked={newTenant.features.enableNotifications}
                       onChange={(e) => setNewTenant({
                         ...newTenant,
                         features: {
                           ...newTenant.features,
                           enableNotifications: e.target.checked
                         }
                       })}
                       className="w-4 h-4 text-blue-600 bg-gray-800 border-gray-400 rounded focus:ring-blue-500 focus:ring-2 hover:bg-gray-700 transition-all duration-200"
                     />
                     <span className="text-sm font-medium">Enable Notifications</span>
                   </label>

                   <label className="checkbox-item flex items-center space-x-3 text-white hover:text-gray-100 cursor-pointer p-2 rounded-lg hover:bg-gray-700 transition-all duration-200">
                     <input
                       type="checkbox"
                       checked={newTenant.features.enableIntegrations}
                       onChange={(e) => setNewTenant({
                         ...newTenant,
                         features: {
                           ...newTenant.features,
                           enableIntegrations: e.target.checked
                         }
                       })}
                       className="w-4 h-4 text-blue-600 bg-gray-800 border-gray-400 rounded focus:ring-blue-500 focus:ring-2 hover:bg-gray-700 transition-all duration-200"
                     />
                     <span className="text-sm font-medium">Enable Integrations</span>
                   </label>

                   <label className="checkbox-item flex items-center space-x-3 text-white hover:text-gray-100 cursor-pointer p-2 rounded-lg hover:bg-gray-700 transition-all duration-200">
                     <input
                       type="checkbox"
                       checked={newTenant.features.enableCustomReports}
                       onChange={(e) => setNewTenant({
                         ...newTenant,
                         features: {
                           ...newTenant.features,
                           enableCustomReports: e.target.checked
                         }
                       })}
                       className="w-4 h-4 text-blue-600 bg-gray-800 border-gray-400 rounded focus:ring-blue-500 focus:ring-2 hover:bg-gray-700 transition-all duration-200"
                     />
                     <span className="text-sm font-medium">Enable Custom Reports</span>
                   </label>

                   <label className="checkbox-item flex items-center space-x-3 text-white hover:text-gray-100 cursor-pointer p-2 rounded-lg hover:bg-gray-700 transition-all duration-200">
                     <input
                       type="checkbox"
                       checked={newTenant.features.enableAPIAccess}
                       onChange={(e) => setNewTenant({
                         ...newTenant,
                         features: {
                           ...newTenant.features,
                           enableAPIAccess: e.target.checked
                         }
                       })}
                       className="w-4 h-4 text-blue-600 bg-gray-800 border-gray-400 rounded focus:ring-blue-500 focus:ring-2 hover:bg-gray-700 transition-all duration-200"
                     />
                     <span className="text-sm font-medium">Enable API Access</span>
                   </label>
                 </div>
               </div>

               {/* Subscription Section */}
               <div className="form-section bg-gray-800 border border-gray-600 rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-200">
                 <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
                   <span className="w-2 h-2 bg-indigo-500 rounded-full mr-3"></span>
                   Subscription
                 </h4>
                 <div className="form-row grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="form-group">
                    <label className="block text-sm font-medium text-white mb-2">Plan</label>
                    <select
                      value={newTenant.subscription.plan}
                      onChange={(e) => setNewTenant({
                        ...newTenant,
                        subscription: {
                          ...newTenant.subscription,
                          plan: e.target.value
                        }
                      })}
                      className="w-full px-4 py-3 border border-gray-500 rounded-lg bg-gray-800 text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-gray-400"
                    >
                      {tenantConfig.subscriptionPlans.map((plan) => (
                        <option key={plan.value} value={plan.value}>
                          {plan.label}
                        </option>
                      ))}
                    </select>
                  </div>

                   <div className="form-group">
                     <label className="block text-sm font-medium text-white mb-2">Max Users</label>
                     <input
                       type="number"
                       value={newTenant.subscription.maxUsers}
                       onChange={(e) => setNewTenant({
                         ...newTenant,
                         subscription: {
                           ...newTenant.subscription,
                           maxUsers: parseInt(e.target.value) || 0
                         }
                       })}
                       min="1"
                       className="w-full px-4 py-3 border border-gray-500 rounded-lg bg-gray-800 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-gray-400"
                     />
                   </div>

                  <div className="form-group">
                    <label className="block text-sm font-medium text-white mb-2">Storage Limit</label>
                    <select
                      value={newTenant.subscription.storageLimit}
                      onChange={(e) => setNewTenant({
                        ...newTenant,
                        subscription: {
                          ...newTenant.subscription,
                          storageLimit: e.target.value
                        }
                      })}
                      className="w-full px-4 py-3 border border-gray-500 rounded-lg bg-gray-800 text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-gray-400"
                    >
                      {tenantConfig.storageLimits.map((limit) => (
                        <option key={limit.value} value={limit.value}>
                          {limit.label}
                        </option>
                      ))}
                    </select>
                  </div>
                 </div>

                 <div className="form-group mt-4">
                   <label className="block text-sm font-medium text-white mb-2">Subscription Expiry Date</label>
                   <input
                     type="date"
                     value={newTenant.subscription.expiryDate}
                     onChange={(e) => setNewTenant({
                       ...newTenant,
                       subscription: {
                         ...newTenant.subscription,
                         expiryDate: e.target.value
                       }
                     })}
                     className="w-full px-3 py-2 border border-gray-500 rounded-lg bg-gray-800 text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                   />
                 </div>
               </div>

               <div className="form-group checkbox-group bg-gray-800 border border-gray-600 rounded-xl p-4 shadow-lg hover:shadow-xl transition-shadow duration-200">
                 <label className="flex items-center space-x-3 text-white hover:text-gray-100 cursor-pointer">
                   <input
                     type="checkbox"
                     checked={newTenant.isDefault}
                     onChange={(e) => setNewTenant({
                       ...newTenant,
                       isDefault: e.target.checked
                     })}
                     className="w-4 h-4 text-blue-600 bg-gray-800 border-gray-400 rounded focus:ring-blue-500 focus:ring-2 hover:bg-gray-700 transition-all duration-200"
                   />
                   <span className="text-sm font-medium">Set as global default tenant</span>
                 </label>
               </div>

              <div className="modal-actions flex justify-end space-x-3 pt-6 border-t border-gray-600">
                <button 
                  type="button" 
                  className="cancel-btn bg-gray-700 hover:bg-gray-600 text-white px-6 py-3 rounded-lg transition-all duration-200 hover:scale-105 font-medium"
                  onClick={() => setActiveModal(null)}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="create-btn bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-6 py-3 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105 font-medium shadow-lg hover:shadow-xl"
                  disabled={loading}
                >
                  {loading ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Creating...
                    </span>
                  ) : (
                    'Create Tenant'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

             {/* Assign User Modal */}
       {activeModal === 'assign' && selectedTenant && (
         <div className="modal-overlay fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
           <div className="modal bg-gray-800 border border-gray-700 rounded-lg max-w-2xl w-full">
             <div className="modal-header bg-gray-700 border-b border-gray-600 p-6 flex items-center justify-between">
               <h3 className="text-xl font-semibold text-white">Assign User to {selectedTenant.name}</h3>
               <button 
                 className="close-btn text-gray-400 hover:text-white p-2 hover:bg-gray-600 rounded-lg transition-colors"
                 onClick={() => setActiveModal(null)}
               >
                 ×
               </button>
             </div>

             <form onSubmit={handleAssignUser} className="modal-form p-6 space-y-6">
               <div className="form-group">
                 <label className="block text-sm font-medium text-white mb-2">Select User *</label>
                 <select
                   value={userAssignment.userId}
                   onChange={(e) => setUserAssignment({
                     ...userAssignment,
                     userId: e.target.value
                   })}
                   required
                   className="w-full px-3 py-2 border border-gray-500 rounded-lg bg-gray-800 text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                 >
                   <option value="">Choose a user...</option>
                   {users.map((user) => (
                     <option key={user.id} value={user.id}>
                       {user.firstName} {user.lastName} ({user.username})
                     </option>
                   ))}
                 </select>
               </div>

               <div className="form-group">
                 <label className="block text-sm font-medium text-white mb-2">Access Level *</label>
                 <select
                   value={userAssignment.accessLevel}
                   onChange={(e) => setUserAssignment({
                     ...userAssignment,
                     accessLevel: e.target.value
                   })}
                   required
                   className="w-full px-3 py-2 border border-gray-500 rounded-lg bg-gray-800 text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                 >
                   <option value="Read">Read Access</option>
                   <option value="Write">Write Access</option>
                   <option value="Admin">Admin Access</option>
                 </select>
               </div>

               <div className="form-group checkbox-group bg-gray-700 rounded-lg p-4">
                 <label className="flex items-center space-x-3 text-white hover:text-gray-100 cursor-pointer">
                   <input
                     type="checkbox"
                     checked={userAssignment.isDefault}
                     onChange={(e) => setUserAssignment({
                       ...userAssignment,
                       isDefault: e.target.checked
                     })}
                     className="w-4 h-4 text-blue-600 bg-gray-600 border-gray-500 rounded focus:ring-blue-500 focus:ring-2"
                   />
                   <span>Set as user's default tenant</span>
                 </label>
               </div>

               <div className="modal-actions flex justify-end space-x-3 pt-6 border-t border-gray-600">
                 <button 
                   type="button" 
                   className="cancel-btn bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors"
                   onClick={() => setActiveModal(null)}
                 >
                   Cancel
                 </button>
                 <button 
                   type="submit" 
                   className="assign-btn bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                   disabled={loading}
                 >
                   {loading ? 'Assigning...' : 'Assign User'}
                 </button>
               </div>
             </form>
           </div>
         </div>
       )}

             {/* Tenant Details Modal */}
       {activeModal === 'details' && selectedTenant && (
         <div className="modal-overlay fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
           <div className="modal large bg-gray-800 border border-gray-700 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
             <div className="modal-header bg-gray-700 border-b border-gray-600 p-6 flex items-center justify-between">
               <h3 className="text-xl font-semibold text-white">{selectedTenant.companyName || selectedTenant.name} Details</h3>
               <button 
                 className="close-btn text-gray-400 hover:text-white p-2 hover:bg-gray-600 rounded-lg transition-colors"
                 onClick={() => setActiveModal(null)}
               >
                 ×
               </button>
             </div>

             <div className="tenant-details p-6 space-y-6">
               <div className="details-section bg-gray-700 rounded-lg p-6">
                 <h4 className="text-lg font-semibold text-white mb-4">Basic Information</h4>
                 <div className="details-grid grid grid-cols-1 md:grid-cols-2 gap-4">
                   <div className="detail-item flex justify-between items-center">
                     <span className="detail-label text-white font-medium">Code:</span>
                     <span className="detail-value text-white font-medium">{selectedTenant.code}</span>
                   </div>
                   <div className="detail-item flex justify-between items-center">
                     <span className="detail-label text-white font-medium">Name:</span>
                     <span className="detail-value text-white font-medium">{selectedTenant.name}</span>
                   </div>
                   <div className="detail-item flex justify-between items-center">
                     <span className="detail-label text-white font-medium">Company:</span>
                     <span className="detail-value text-white font-medium">{selectedTenant.companyName || 'N/A'}</span>
                   </div>
                   <div className="detail-item flex justify-between items-center">
                     <span className="detail-label text-white font-medium">Description:</span>
                     <span className="detail-value text-white font-medium">
                       {selectedTenant.description || 'No description'}
                     </span>
                   </div>
                   <div className="detail-item flex justify-between items-center">
                     <span className="detail-label text-white font-medium">Your Access:</span>
                     <span className={`access-badge px-2 py-1 rounded text-xs font-medium ${selectedTenant.accessLevel.toLowerCase() === 'admin' ? 'bg-purple-600 text-white' : selectedTenant.accessLevel.toLowerCase() === 'write' ? 'bg-blue-600 text-white' : 'bg-gray-600 text-white'}`}>
                       {selectedTenant.accessLevel}
                     </span>
                   </div>
                 </div>
               </div>

               <div className="details-section bg-gray-700 rounded-lg p-6">
                 <h4 className="text-lg font-semibold text-white mb-4">Contact Information</h4>
                 <div className="details-grid grid grid-cols-1 md:grid-cols-2 gap-4">
                   <div className="detail-item flex justify-between items-center">
                     <span className="detail-label text-white font-medium">Admin Email:</span>
                     <span className="detail-value text-white font-medium">{selectedTenant.adminEmail || 'N/A'}</span>
                   </div>
                   <div className="detail-item flex justify-between items-center">
                     <span className="detail-label text-white font-medium">Primary Contact:</span>
                     <span className="detail-value text-white font-medium">{selectedTenant.primaryContact || 'N/A'}</span>
                   </div>
                   <div className="detail-item flex justify-between items-center">
                     <span className="detail-label text-white font-medium">Phone:</span>
                     <span className="detail-value text-white font-medium">{selectedTenant.phoneNumber || 'N/A'}</span>
                   </div>
                   <div className="detail-item flex justify-between items-center">
                     <span className="detail-label text-white font-medium">Website:</span>
                     <span className="detail-value text-white font-medium">
                       {selectedTenant.website ? (
                         <a href={selectedTenant.website} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 underline">
                           {selectedTenant.website}
                         </a>
                       ) : 'N/A'}
                     </span>
                   </div>
                 </div>
               </div>

               <div className="details-section bg-gray-700 rounded-lg p-6">
                 <h4 className="text-lg font-semibold text-white mb-4">Configuration</h4>
                 <div className="details-grid grid grid-cols-1 md:grid-cols-3 gap-4">
                   <div className="detail-item flex justify-between items-center">
                     <span className="detail-label text-white font-medium">Timezone:</span>
                     <span className="detail-value text-white font-medium">{selectedTenant.timezone || 'UTC'}</span>
                   </div>
                   <div className="detail-item flex justify-between items-center">
                     <span className="detail-label text-white font-medium">Currency:</span>
                     <span className="detail-value text-white font-medium">{selectedTenant.currency || 'USD'}</span>
                   </div>
                   <div className="detail-item flex justify-between items-center">
                     <span className="detail-label text-white font-medium">Language:</span>
                     <span className="detail-value text-white font-medium">{selectedTenant.language || 'en'}</span>
                   </div>
                 </div>
               </div>

               <div className="details-section bg-gray-700 rounded-lg p-6">
                 <h4 className="text-lg font-semibold text-white mb-4">Database Configuration</h4>
                 <div className="details-grid grid grid-cols-1 md:grid-cols-2 gap-4">
                   <div className="detail-item flex justify-between items-center">
                     <span className="detail-label text-white font-medium">Database Server:</span>
                     <span className="detail-value text-white font-medium">{selectedTenant.databaseServer || 'N/A'}</span>
                   </div>
                   <div className="detail-item flex justify-between items-center">
                     <span className="detail-label text-white font-medium">Database Name:</span>
                     <span className="detail-value text-white font-medium">{selectedTenant.databaseName || 'N/A'}</span>
                   </div>
                   <div className="detail-item flex justify-between items-center">
                     <span className="detail-label text-white font-medium">Database Username:</span>
                     <span className="detail-value text-white font-medium">{selectedTenant.databaseUsername || 'N/A'}</span>
                   </div>
                   <div className="detail-item flex justify-between items-center">
                     <span className="detail-label text-white font-medium">Database Password:</span>
                     <span className="detail-value text-white font-medium">
                       {selectedTenant.databasePassword ? '••••••••' : 'N/A'}
                     </span>
                   </div>
                   <div className="detail-item flex justify-between items-center">
                     <span className="detail-label text-white font-medium">Upload Directory:</span>
                     <span className="detail-value text-white font-medium">{selectedTenant.uploadDirectory || 'N/A'}</span>
                   </div>
                   <div className="detail-item flex justify-between items-center">
                     <span className="detail-label text-white font-medium">AI Config Path:</span>
                     <span className="detail-value text-white font-medium">{selectedTenant.aiConfigPath || 'N/A'}</span>
                   </div>
                 </div>
               </div>

               <div className="details-section bg-gray-700 rounded-lg p-6">
                 <h4 className="text-lg font-semibold text-white mb-4">Email Domain Configuration</h4>
                 <div className="details-grid grid grid-cols-1 gap-4">
                   <div className="detail-item">
                     <span className="detail-label text-white font-medium block mb-2">Email Domains:</span>
                     <div className="detail-value">
                       {selectedTenant.emailDomains && selectedTenant.emailDomains.length > 0 ? (
                         <div className="flex flex-wrap gap-2">
                           {selectedTenant.emailDomains.map((domain, index) => (
                             <span 
                               key={index}
                               className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                             >
                               @{domain}
                             </span>
                           ))}
                         </div>
                       ) : (
                         <span className="text-gray-400 italic">No email domains configured</span>
                       )}
                     </div>
                   </div>
                   <div className="detail-item">
                     <span className="detail-label text-white font-medium block mb-2">Auto-Assignment:</span>
                     <div className="detail-value">
                       <span className="text-sm text-gray-300">
                         {selectedTenant.emailDomains && selectedTenant.emailDomains.length > 0 
                           ? `Users with these email domains will be automatically assigned to this tenant`
                           : `Users will be assigned to DEMO tenant by default`
                         }
                       </span>
                     </div>
                   </div>
                 </div>
               </div>

               {selectedTenant.subscription && (
                 <div className="details-section bg-gray-700 rounded-lg p-6">
                   <h4 className="text-lg font-semibold text-white mb-4">Subscription</h4>
                   <div className="details-grid grid grid-cols-1 md:grid-cols-3 gap-4">
                     <div className="detail-item flex justify-between items-center">
                       <span className="detail-label text-white font-medium">Plan:</span>
                       <span className="subscription-badge bg-green-600 text-white px-2 py-1 rounded text-xs font-medium">{selectedTenant.subscription.plan}</span>
                     </div>
                     <div className="detail-item flex justify-between items-center">
                       <span className="detail-label text-white font-medium">Max Users:</span>
                       <span className="detail-value text-white font-medium">{selectedTenant.subscription.maxUsers}</span>
                     </div>
                     <div className="detail-item flex justify-between items-center">
                       <span className="detail-label text-white font-medium">Storage Limit:</span>
                       <span className="detail-value text-white font-medium">{selectedTenant.subscription.storageLimit}</span>
                     </div>
                   </div>
                 </div>
               )}

               <div className="details-section bg-gray-700 rounded-lg p-6">
                 <h4 className="text-lg font-semibold text-white mb-4">Access Information</h4>
                 <p className="text-white">To view user assignments and detailed statistics, you need tenant admin access.</p>
               </div>
             </div>

             <div className="modal-actions flex justify-end p-6 border-t border-gray-600">
               <button 
                 className="close-btn bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors"
                 onClick={() => setActiveModal(null)}
               >
                 Close
               </button>
             </div>
           </div>
         </div>
       )}

             {/* Edit Tenant Modal */}
       {activeModal === 'edit' && selectedTenant && (
         <div className="modal-overlay fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
           <div className="modal large bg-gray-800 border border-gray-700 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
             <div className="modal-header bg-gray-700 border-b border-gray-600 p-6 flex items-center justify-between">
               <h3 className="text-xl font-semibold text-white">Edit Tenant: {selectedTenant.name}</h3>
               <button 
                 className="close-btn text-gray-400 hover:text-white p-2 hover:bg-gray-600 rounded-lg transition-colors"
                 onClick={() => setActiveModal(null)}
               >
                 ×
               </button>
             </div>

                         <form onSubmit={handleEditTenant} className="modal-form p-6 space-y-6">
               <div className="form-section bg-gray-700 rounded-lg p-6">
                 <h4 className="text-lg font-semibold text-white mb-4">Basic Information</h4>
                 <div className="form-row grid grid-cols-1 md:grid-cols-2 gap-4">
                   <div className="form-group">
                     <label className="block text-sm font-medium text-white mb-2">Tenant Code *</label>
                     <input
                       type="text"
                       value={editTenant.tenantCode}
                       disabled
                       className="w-full px-3 py-2 border border-gray-600 rounded-lg bg-gray-600 text-gray-400 cursor-not-allowed"
                     />
                     <small className="text-gray-300 text-xs">Tenant code cannot be changed</small>
                   </div>

                                     <div className="form-group">
                     <label className="block text-sm font-medium text-white mb-2">Tenant Name *</label>
                     <input
                       type="text"
                       value={editTenant.tenantName}
                       onChange={(e) => setEditTenant({
                         ...editTenant,
                         tenantName: e.target.value
                       })}
                       required
                       className="w-full px-4 py-3 border border-gray-500 rounded-lg bg-gray-800 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-gray-400"
                     />
                   </div>
                 </div>

                 <div className="form-group mt-4">
                   <label className="block text-sm font-medium text-white mb-2">Description</label>
                   <textarea
                     value={editTenant.description}
                     onChange={(e) => setEditTenant({
                       ...editTenant,
                       description: e.target.value
                     })}
                     rows="3"
                     className="w-full px-3 py-2 border border-gray-500 rounded-lg bg-gray-800 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                   />
                 </div>
               </div>

                             <div className="form-section bg-gray-700 rounded-lg p-6">
                 <h4 className="text-lg font-semibold text-white mb-4">Company Information</h4>
                 <div className="form-row grid grid-cols-1 md:grid-cols-2 gap-4">
                   <div className="form-group">
                     <label className="block text-sm font-medium text-white mb-2">Company Name</label>
                     <input
                       type="text"
                       value={editTenant.companyName}
                       onChange={(e) => setEditTenant({
                         ...editTenant,
                         companyName: e.target.value
                       })}
                       className="w-full px-4 py-3 border border-gray-500 rounded-lg bg-gray-800 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-gray-400"
                     />
                   </div>

                                     <div className="form-group">
                     <label className="block text-sm font-medium text-white mb-2">Admin Email</label>
                     <input
                       type="email"
                       value={editTenant.adminEmail}
                       onChange={(e) => setEditTenant({
                         ...editTenant,
                         adminEmail: e.target.value
                       })}
                       className="w-full px-4 py-3 border border-gray-500 rounded-lg bg-gray-800 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-gray-400"
                     />
                   </div>
                 </div>

                 <div className="form-row grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                   <div className="form-group">
                     <label className="block text-sm font-medium text-white mb-2">Primary Contact</label>
                     <input
                       type="text"
                       value={editTenant.primaryContact}
                       onChange={(e) => setEditTenant({
                         ...editTenant,
                         primaryContact: e.target.value
                       })}
                       className="w-full px-4 py-3 border border-gray-500 rounded-lg bg-gray-800 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-gray-400"
                     />
                   </div>

                   <div className="form-group">
                     <label className="block text-sm font-medium text-white mb-2">Phone Number</label>
                     <input
                       type="tel"
                       value={editTenant.phoneNumber}
                       onChange={(e) => setEditTenant({
                         ...editTenant,
                         phoneNumber: e.target.value
                       })}
                       className="w-full px-4 py-3 border border-gray-500 rounded-lg bg-gray-800 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-gray-400"
                     />
                   </div>
                 </div>

                                 <div className="form-row grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                   <div className="form-group">
                     <label className="block text-sm font-medium text-white mb-2">Website</label>
                     <input
                       type="url"
                       value={editTenant.website}
                       onChange={(e) => setEditTenant({
                         ...editTenant,
                         website: e.target.value
                       })}
                       className="w-full px-4 py-3 border border-gray-500 rounded-lg bg-gray-800 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-gray-400"
                     />
                   </div>

                   <div className="form-group">
                     <label className="block text-sm font-medium text-white mb-2">Company Logo URL</label>
                     <input
                       type="url"
                       value={editTenant.companyLogo}
                       onChange={(e) => setEditTenant({
                         ...editTenant,
                         companyLogo: e.target.value
                       })}
                       className="w-full px-4 py-3 border border-gray-500 rounded-lg bg-gray-800 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-gray-400"
                     />
                   </div>
                 </div>

                 <div className="form-group mt-4">
                   <label className="block text-sm font-medium text-white mb-2">Address</label>
                   <textarea
                     value={editTenant.address}
                     onChange={(e) => setEditTenant({
                       ...editTenant,
                       address: e.target.value
                     })}
                     rows="2"
                     className="w-full px-3 py-2 border border-gray-500 rounded-lg bg-gray-800 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                   />
                 </div>
               </div>

                             <div className="form-section bg-gray-700 rounded-lg p-6">
                 <h4 className="text-lg font-semibold text-white mb-4">Database Configuration</h4>

                 {/* Database Type Selection */}
                 <div className="form-group mb-4">
                   <label className="block text-sm font-medium text-white mb-2">Database Type</label>
                   <div className="database-type-selector flex space-x-4">
                     {tenantConfig.databaseTypes.map((dbType) => (
                       <label key={dbType.value} className="flex items-center space-x-2 text-white hover:text-gray-100 cursor-pointer">
                         <input
                           type="radio"
                           name="editDatabaseType"
                           value={dbType.value}
                           checked={editTenant.databaseType === dbType.value}
                           onChange={(e) => {
                             const selectedDbType = tenantConfig.databaseTypes.find(dt => dt.value === e.target.value);
                             setEditTenant({
                               ...editTenant,
                               databaseType: e.target.value,
                               // Reset database-specific fields when switching types
                               databaseServer: 'localhost',
                               databaseUsername: selectedDbType?.defaultUsername || 'sa',
                               databasePassword: '',
                               databasePort: selectedDbType?.defaultPort?.toString() || '1433'
                             });
                           }}
                           className="w-4 h-4 text-blue-600 bg-gray-800 border-gray-400 rounded focus:ring-blue-500 focus:ring-2 hover:bg-gray-700 transition-all duration-200"
                         />
                         <span className="flex items-center space-x-2">
                           <span className={`w-6 h-6 ${dbType.value === 'sqlserver' ? 'bg-blue-600' : 'bg-green-600'} rounded flex items-center justify-center text-white text-xs font-bold`}>
                             {dbType.value === 'sqlserver' ? 'SQL' : 'PG'}
                           </span>
                           <span>{dbType.label}</span>
                         </span>
                       </label>
                     ))}
                   </div>
                   <small className="text-gray-300 text-xs mt-1">
                     Select the database type for this tenant
                   </small>
                 </div>

                 <div className="form-row grid grid-cols-1 md:grid-cols-2 gap-4">
                   <div className="form-group">
                     <label className="block text-sm font-medium text-white mb-2">Database Name *</label>
                     <input
                       type="text"
                       value={editTenant.databaseName}
                       onChange={(e) => setEditTenant({
                         ...editTenant,
                         databaseName: e.target.value
                       })}
                       placeholder={editTenant.databaseType === 'postgresql' ? 'e.g., crm_client1_db' : 'e.g., CRM_CLIENT1_DB'}
                       required
                       className="w-full px-4 py-3 border border-gray-500 rounded-lg bg-gray-800 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-gray-400"
                     />
                   </div>

                   <div className="form-group">
                     <label className="block text-sm font-medium text-white mb-2">Upload Directory *</label>
                     <input
                       type="text"
                       value={editTenant.uploadDirectory}
                       onChange={(e) => setEditTenant({
                         ...editTenant,
                         uploadDirectory: e.target.value
                       })}
                       placeholder="e.g., uploads/client1"
                       required
                       className="w-full px-4 py-3 border border-gray-500 rounded-lg bg-gray-800 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-gray-400"
                     />
                   </div>
                 </div>

                 {/* Database-specific configuration */}
                 {editTenant.databaseType === 'postgresql' && (
                   <>
                     <div className="form-row grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                       <div className="form-group">
                         <label className="block text-sm font-medium text-white mb-2">Database Username</label>
                         <input
                           type="text"
                           value={editTenant.databaseUsername}
                           onChange={(e) => setEditTenant({
                             ...editTenant,
                             databaseUsername: e.target.value
                           })}
                           placeholder="e.g., postgres"
                           className="w-full px-4 py-3 border border-gray-500 rounded-lg bg-gray-800 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-gray-400"
                         />
                       </div>

                       <div className="form-group">
                         <label className="block text-sm font-medium text-white mb-2">Database Password</label>
                         <input
                           type="password"
                           value={editTenant.databasePassword}
                           onChange={(e) => setEditTenant({
                             ...editTenant,
                             databasePassword: e.target.value
                           })}
                           placeholder="Enter PostgreSQL password"
                           className="w-full px-4 py-3 border border-gray-500 rounded-lg bg-gray-800 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-gray-400"
                         />
                       </div>
                     </div>
                     <small className="text-gray-300 text-xs mt-2 block">
                       PostgreSQL superuser credentials required for database creation and setup
                     </small>
                   </>
                 )}

                 <div className="form-row grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                   <div className="form-group">
                     <label className="block text-sm font-medium text-white mb-2">Database Server</label>
                     <input
                       type="text"
                       value={editTenant.databaseServer}
                       onChange={(e) => setEditTenant({
                         ...editTenant,
                         databaseServer: e.target.value
                       })}
                       placeholder={editTenant.databaseType === 'postgresql' ? 'localhost:5432' : 'localhost'}
                       className="w-full px-4 py-3 border border-gray-500 rounded-lg bg-gray-800 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-gray-400"
                     />
                     <small className="text-gray-300 text-xs mt-1">
                       {editTenant.databaseType === 'postgresql'
                         ? 'PostgreSQL server host and port (default: localhost:5432)'
                         : 'SQL Server host (default: localhost)'}
                     </small>
                   </div>

                   <div className="form-group">
                     <label className="block text-sm font-medium text-white mb-2">Port</label>
                     <input
                       type="number"
                       value={editTenant.databasePort || (editTenant.databaseType === 'postgresql' ? '5432' : '1433')}
                       onChange={(e) => setEditTenant({
                         ...editTenant,
                         databasePort: e.target.value
                       })}
                       placeholder={editTenant.databaseType === 'postgresql' ? '5432' : '1433'}
                       min="1"
                       max="65535"
                       className="w-full px-4 py-3 border border-gray-500 rounded-lg bg-gray-800 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-gray-400"
                     />
                     <small className="text-gray-300 text-xs mt-1">
                       {editTenant.databaseType === 'postgresql'
                         ? 'PostgreSQL port (default: 5432)'
                         : 'SQL Server port (default: 1433)'}
                     </small>
                   </div>
                 </div>
               </div>

                             <div className="form-section bg-gray-700 rounded-lg p-6">
                 <h4 className="text-lg font-semibold text-white mb-4">Settings</h4>
                 <div className="form-row grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="form-group">
                    <label className="block text-sm font-medium text-white mb-2">Timezone</label>
                    <select
                      value={editTenant.timezone}
                      onChange={(e) => setEditTenant({
                        ...editTenant,
                        timezone: e.target.value
                      })}
                      className="w-full px-4 py-3 border border-gray-500 rounded-lg bg-gray-800 text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-gray-400"
                    >
                      {tenantConfig.timezones.map((tz) => (
                        <option key={tz.value} value={tz.value}>
                          {tz.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="block text-sm font-medium text-white mb-2">Currency</label>
                    <select
                      value={editTenant.currency}
                      onChange={(e) => setEditTenant({
                        ...editTenant,
                        currency: e.target.value
                      })}
                      className="w-full px-4 py-3 border border-gray-500 rounded-lg bg-gray-800 text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-gray-400"
                    >
                      {tenantConfig.currencies.map((curr) => (
                        <option key={curr.value} value={curr.value}>
                          {curr.label}
                        </option>
                      ))}
                    </select>
                  </div>
                 </div>

                <div className="form-group mt-4">
                  <label className="block text-sm font-medium text-white mb-2">Language</label>
                  <select
                    value={editTenant.language}
                    onChange={(e) => setEditTenant({
                      ...editTenant,
                      language: e.target.value
                    })}
                    className="w-full px-3 py-2 border border-gray-500 rounded-lg bg-gray-800 text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {tenantConfig.languages.map((lang) => (
                      <option key={lang.value} value={lang.value}>
                        {lang.label}
                      </option>
                    ))}
                  </select>
                </div>

                 <div className="form-group checkbox-group bg-gray-600 rounded-lg p-4 mt-4">
                   <label className="flex items-center space-x-3 text-white hover:text-gray-100 cursor-pointer">
                     <input
                       type="checkbox"
                       checked={editTenant.isActive}
                       onChange={(e) => setEditTenant({
                         ...editTenant,
                         isActive: e.target.checked
                       })}
                       className="w-4 h-4 text-blue-600 bg-gray-800 border-gray-400 rounded focus:ring-blue-500 focus:ring-2 hover:bg-gray-700 transition-all duration-200"
                     />
                     <span>Tenant is active</span>
                   </label>
                 </div>
               </div>

               <div className="modal-actions flex justify-end space-x-3 pt-6 border-t border-gray-600">
                 <button 
                   type="button" 
                   className="cancel-btn bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors"
                   onClick={() => setActiveModal(null)}
                 >
                   Cancel
                 </button>
                 <button 
                   type="submit" 
                   className="save-btn bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                   disabled={loading}
                 >
                   {loading ? 'Saving...' : 'Save Changes'}
                 </button>
               </div>
            </form>
          </div>
        </div>
      )}

             {/* Delete Tenant Modal */}
       {activeModal === 'delete' && selectedTenant && (
         <div className="modal-overlay fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
           <div className="modal bg-gray-800 border border-gray-700 rounded-lg max-w-2xl w-full">
             <div className="modal-header bg-gray-700 border-b border-gray-600 p-6 flex items-center justify-between">
               <h3 className="text-xl font-semibold text-white">Delete Tenant</h3>
               <button 
                 className="close-btn text-gray-400 hover:text-white p-2 hover:bg-gray-600 rounded-lg transition-colors"
                 onClick={() => setActiveModal(null)}
               >
                 ×
               </button>
             </div>

                         <div className="modal-content p-6">
               <div className="warning-content bg-yellow-900/20 border border-yellow-800 rounded-lg p-4 mb-6">
                 <div className="flex items-center space-x-3">
                   <div className="warning-icon text-2xl">⚠️</div>
                   <div className="warning-text">
                     <h4 className="text-lg font-semibold text-yellow-400 mb-2">Are you sure you want to delete this tenant?</h4>
                     <p className="text-white mb-2"><strong>Tenant:</strong> {selectedTenant.name} ({selectedTenant.code})</p>
                     <p className="warning-message text-gray-300">
                       This action cannot be undone. All tenant data, user assignments, 
                       and configurations will be permanently deleted.
                     </p>
                   </div>
                 </div>
               </div>

               <div className="danger-checklist bg-red-900/20 border border-red-800 rounded-lg p-4">
                 <h5 className="text-lg font-semibold text-red-400 mb-3">This will permanently delete:</h5>
                 <ul className="space-y-2 text-gray-300">
                   <li className="flex items-center space-x-2">✗ <span>All tenant configuration data</span></li>
                   <li className="flex items-center space-x-2">✗ <span>User assignments to this tenant</span></li>
                   <li className="flex items-center space-x-2">✗ <span>Tenant-specific settings</span></li>
                   <li className="flex items-center space-x-2">✗ <span>Access to tenant database</span></li>
                 </ul>
               </div>
             </div>

             <div className="modal-actions flex justify-end space-x-3 p-6 border-t border-gray-600">
               <button 
                 type="button" 
                 className="cancel-btn bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors"
                 onClick={() => setActiveModal(null)}
               >
                 Cancel
               </button>
               <button 
                 type="button" 
                 className="delete-btn bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                 onClick={handleDeleteTenant}
                 disabled={loading}
               >
                 {loading ? 'Deleting...' : 'Delete Tenant'}
               </button>
             </div>
          </div>
        </div>
      )}

      {/* Import Users Modal */}
      {activeModal === 'import' && selectedTenant && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Import Users from {selectedTenant.name}</h3>
              <button 
                className="close-btn"
                onClick={() => setActiveModal(null)}
              >
                ×
              </button>
            </div>

            <div className="modal-content">
              <div className="import-info">
                <h4>Import Process</h4>
                <p>
                  This will scan the tenant's SQL Server database for existing users 
                  and import them into the central user management system.
                </p>
                
                <div className="import-details">
                  <h5>What will be imported:</h5>
                  <ul>
                    <li>✓ User account information (username, email, names)</li>
                    <li>✓ User status (active/inactive)</li>
                    <li>✓ Automatic assignment to this tenant</li>
                  </ul>
                  
                  <h5>What will NOT be imported:</h5>
                  <ul>
                    <li>✗ Passwords (users need to reset)</li>
                    <li>✗ Role assignments</li>
                    <li>✗ User preferences</li>
                  </ul>
                </div>
              </div>

              {!importProgress.isImporting && importProgress.total === 0 && (
                <div className="import-actions">
                  <button 
                    className="import-btn"
                    onClick={handleImportUsers}
                    disabled={loading}
                  >
                    🔄 Start Import
                  </button>
                </div>
              )}

              {importProgress.isImporting && (
                <div className="import-progress">
                  <h4>Importing Users...</h4>
                  <div className="progress-bar">
                    <div 
                      className="progress-fill"
                      style={{ width: `${importProgress.progress}%` }}
                    ></div>
                  </div>
                  <p>Processing users from tenant database...</p>
                </div>
              )}

              {!importProgress.isImporting && importProgress.total > 0 && (
                <div className="import-results">
                  <h4>Import Results</h4>
                  <div className="results-summary">
                    <div className="result-item success">
                      <span className="result-icon">✅</span>
                      <span className="result-text">
                        {importProgress.imported} users imported successfully
                      </span>
                    </div>
                    {importProgress.failed > 0 && (
                      <div className="result-item error">
                        <span className="result-icon">❌</span>
                        <span className="result-text">
                          {importProgress.failed} users failed to import
                        </span>
                      </div>
                    )}
                  </div>

                  {importProgress.errors.length > 0 && (
                    <div className="import-errors">
                      <h5>Errors:</h5>
                      <ul>
                        {importProgress.errors.map((error, index) => (
                          <li key={index} className="error-item">{error}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="modal-actions">
              <button 
                className="close-btn"
                onClick={() => {
                  setActiveModal(null);
                  setImportProgress({
                    isImporting: false,
                    progress: 0,
                    total: 0,
                    imported: 0,
                    failed: 0,
                    errors: []
                  });
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Database Validation Modal - 80% Width */}
      {activeModal === 'validate-db' && selectedTenant && (
        <div className="modal-overlay fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4 validate-db-modal">
          <div 
            className="modal bg-gray-800 border border-gray-700 rounded-lg flex flex-col overflow-hidden shadow-2xl"
            style={{
              width: '80%',
              minWidth: '800px',
              maxWidth: '1400px',
              height: '90vh',
              maxHeight: '90vh'
            }}
          >
            <div className="modal-header bg-gray-700 border-b border-gray-600 p-4 flex items-center justify-between flex-shrink-0">
              <h3 className="text-2xl font-semibold text-white">Database Validation - {selectedTenant.name}</h3>
              <button 
                className="close-btn text-gray-400 hover:text-white p-2 hover:bg-gray-600 rounded-lg transition-colors text-2xl font-bold"
                onClick={() => {
                  setActiveModal(null);
                  setDbValidation({
                    isValidating: false,
                    isInitializing: false,
                    isFixing: false,
                    isAddingExtra: false,
                    isDroppingExtra: false,
                    status: null,
                    expandedTables: [],
                    results: {
                      tablesChecked: 0,
                      tablesValid: 0,
                      tablesMissing: 0,
                      tablesCreated: 0,
                      tablesFailed: 0,
                      columnsChecked: 0,
                      columnsValid: 0,
                      columnsMissing: 0,
                      columnsMismatched: 0,
                      columnsExtra: 0,
                      extraColumns: {},
                      columnsFixed: 0,
                      columnsFailed: 0,
                      columnsAddedToSchema: 0,
                      columnsDropped: 0,
                      seedDataInserted: false,
                      errors: [],
                      warnings: [],
                      tableDetails: {},
                      tableResults: {},
                      tableErrors: [],
                      fixErrors: []
                    }
                  });
                }}
              >
                ×
              </button>
            </div>

            <style>{`
              /* Left and right column scrollbars */
              .validate-db-modal .left-column::-webkit-scrollbar,
              .validate-db-modal .right-column::-webkit-scrollbar {
                width: 12px;
              }
              .validate-db-modal .left-column::-webkit-scrollbar-track,
              .validate-db-modal .right-column::-webkit-scrollbar-track {
                background: #1f2937;
                border-radius: 6px;
                border: 1px solid #374151;
              }
              .validate-db-modal .left-column::-webkit-scrollbar-thumb,
              .validate-db-modal .right-column::-webkit-scrollbar-thumb {
                background: #4b5563;
                border-radius: 6px;
                border: 2px solid #1f2937;
              }
              .validate-db-modal .left-column::-webkit-scrollbar-thumb:hover,
              .validate-db-modal .right-column::-webkit-scrollbar-thumb:hover {
                background: #6b7280;
              }
              
              /* Nested scrollable areas */
              .validate-db-modal .tables-list > div::-webkit-scrollbar,
              .validate-db-modal [class*="overflow-y"]::-webkit-scrollbar {
                width: 10px;
              }
              .validate-db-modal .tables-list > div::-webkit-scrollbar-track,
              .validate-db-modal [class*="overflow-y"]::-webkit-scrollbar-track {
                background: #374151;
                border-radius: 5px;
              }
              .validate-db-modal .tables-list > div::-webkit-scrollbar-thumb,
              .validate-db-modal [class*="overflow-y"]::-webkit-scrollbar-thumb {
                background: #6b7280;
                border-radius: 5px;
              }
              .validate-db-modal .tables-list > div::-webkit-scrollbar-thumb:hover,
              .validate-db-modal [class*="overflow-y"]::-webkit-scrollbar-thumb:hover {
                background: #9ca3af;
              }
            `}</style>
            <div className="modal-content-wrapper flex flex-1 overflow-hidden">
              {/* Left Column - Database Info & Actions */}
              <div className="left-column flex flex-col p-6 space-y-6 w-2/5 border-r border-gray-600 overflow-y-auto" style={{
                scrollbarWidth: 'thin',
                scrollbarColor: '#4b5563 #1f2937',
                WebkitOverflowScrolling: 'touch'
              }}>
                {/* Database Information Confirmation */}
                <div className="db-info-confirmation bg-blue-900/20 border-2 border-blue-600 rounded-lg p-6">
                <h4 className="text-lg font-semibold text-blue-300 mb-4 flex items-center space-x-2">
                  <span>🔍</span>
                  <span>Database Information</span>
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="info-item">
                    <span className="info-label text-gray-300 text-sm font-medium block mb-1">Database Type:</span>
                    <span className="info-value text-white font-semibold text-lg">
                      {selectedTenant.databaseType === 'sqlserver' ? 'SQL Server' : 
                       selectedTenant.databaseType === 'postgresql' ? 'PostgreSQL' : 
                       selectedTenant.databaseType || 'N/A'}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label text-gray-300 text-sm font-medium block mb-1">Database Server:</span>
                    <span className="info-value text-white font-semibold text-lg">
                      {selectedTenant.databaseServer || 'N/A'}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label text-gray-300 text-sm font-medium block mb-1">Database Name:</span>
                    <span className="info-value text-white font-semibold text-lg break-all">
                      {selectedTenant.databaseName || 'N/A'}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label text-gray-300 text-sm font-medium block mb-1">Database Port:</span>
                    <span className="info-value text-white font-semibold text-lg">
                      {selectedTenant.databasePort || (selectedTenant.databaseType === 'postgresql' ? '5432' : '1433')}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label text-gray-300 text-sm font-medium block mb-1">Database Username:</span>
                    <span className="info-value text-white font-semibold text-lg">
                      {selectedTenant.databaseUsername || 'N/A'}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label text-gray-300 text-sm font-medium block mb-1">Connection Status:</span>
                    <span className="info-value">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-600 text-white">
                        ⚠️ Not Tested
                      </span>
                    </span>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-blue-700">
                  <p className="text-sm text-blue-200">
                    <strong>⚠️ Please confirm:</strong> You are about to validate the database schema for the tenant 
                    <strong className="text-white"> "{selectedTenant.name}"</strong> using the database connection details shown above.
                  </p>
                </div>
              </div>

              <div className="db-validation-info bg-gray-700 rounded-lg p-6">
                <h4 className="text-lg font-semibold text-white mb-4">Database Schema Validation</h4>
                <p className="text-gray-300 mb-4">
                  This will check the tenant's database schema and ensure all required 
                  tables and structures are in place. If issues are found, you can 
                  initialize the database with the correct schema and seed data.
                </p>
                
                <div className="validation-details">
                  <h5 className="text-md font-medium text-white mb-3">Validation Process:</h5>
                  <ul className="space-y-2 text-gray-300">
                    <li className="flex items-center space-x-2">✓ <span>Check database connection</span></li>
                    <li className="flex items-center space-x-2">✓ <span>Validate core CRM tables</span></li>
                    <li className="flex items-center space-x-2">✓ <span>Check table structures and constraints</span></li>
                    <li className="flex items-center space-x-2">✓ <span>Verify indexes and relationships</span></li>
                    <li className="flex items-center space-x-2">✓ <span>Check for required seed data</span></li>
                  </ul>
                </div>
              </div>

              {/* Validation Actions */}
              {!dbValidation.isValidating && !dbValidation.isInitializing && !dbValidation.status && (
                <div className="validation-actions bg-gray-700 rounded-lg p-6">
                  <button 
                    className="validate-btn bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={handleValidateDatabase}
                    disabled={loading}
                  >
                    🔍 Start Validation
                  </button>
                </div>
              )}


              </div>

              {/* Right Column - Validation Results */}
              {!dbValidation.isValidating && !dbValidation.isInitializing && dbValidation.status && (
                <div 
                  className="right-column flex flex-col p-6 space-y-6 w-3/5 overflow-y-scroll" 
                  style={{
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#4b5563 #1f2937',
                    WebkitOverflowScrolling: 'touch',
                    minHeight: 0
                  }}
                >
                  <div className="validation-results bg-gray-700 rounded-lg p-6">
                  <h4 className="text-lg font-semibold text-white mb-4">Validation Results</h4>
                  
                  {dbValidation.status === 'valid' && (
                    <div className="result-item success bg-green-900/20 border border-green-800 rounded-lg p-4 mb-4">
                      <div className="flex items-center space-x-3">
                        <span className="result-icon text-2xl">✅</span>
                        <span className="result-text text-green-400 font-medium">
                          Database schema is valid and complete!
                        </span>
                      </div>
                    </div>
                  )}

                  {dbValidation.status === 'invalid' && (
                    <div className="result-item warning bg-yellow-900/20 border border-yellow-800 rounded-lg p-4 mb-4">
                      <div className="flex items-center space-x-3">
                        <span className="result-icon text-2xl">⚠️</span>
                        <span className="result-text text-yellow-400 font-medium">
                          Database schema has issues that need to be fixed
                        </span>
                      </div>
                    </div>
                  )}

                  {dbValidation.status === 'missing' && (
                    <div className="result-item error bg-red-900/20 border border-red-800 rounded-lg p-4 mb-4">
                      <div className="flex items-center space-x-3">
                        <span className="result-icon text-2xl">❌</span>
                        <span className="result-text text-red-400 font-medium">
                          Database is missing or cannot be accessed
                        </span>
                      </div>
                    </div>
                  )}

                  {dbValidation.status === 'error' && (
                    <div className="result-item error bg-red-900/20 border border-red-800 rounded-lg p-4 mb-4">
                      <div className="flex items-center space-x-3">
                        <span className="result-icon text-2xl">❌</span>
                        <span className="result-text text-red-400 font-medium">
                          Error occurred during validation
                        </span>
                      </div>
                    </div>
                  )}

                  <div className="results-summary bg-gray-600 rounded-lg p-4 mb-4">
                    <h5 className="text-md font-medium text-white mb-3">Summary</h5>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="summary-item flex justify-between">
                        <span className="summary-label text-gray-300">Tables Checked:</span>
                        <span className="summary-value text-white font-medium">{dbValidation.results.tablesChecked}</span>
                      </div>
                      <div className="summary-item flex justify-between">
                        <span className="summary-label text-gray-300">Tables Valid:</span>
                        <span className="summary-value text-green-400 font-medium">{dbValidation.results.tablesValid}</span>
                      </div>
                      {dbValidation.results.tablesMissing > 0 && (
                        <div className="summary-item flex justify-between">
                          <span className="summary-label text-gray-300">Tables Missing:</span>
                          <span className="summary-value text-red-400 font-medium">{dbValidation.results.tablesMissing}</span>
                        </div>
                      )}
                      {dbValidation.results.tablesCreated > 0 && (
                        <div className="summary-item flex justify-between">
                          <span className="summary-label text-gray-300">Tables Created:</span>
                          <span className="summary-value text-green-400 font-medium">{dbValidation.results.tablesCreated}</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Column Validation Summary */}
                    {(dbValidation.results.columnsChecked > 0 || dbValidation.results.columnsMissing > 0 || dbValidation.results.columnsMismatched > 0) && (
                      <div className="mt-4 pt-4 border-t border-gray-500">
                        <h6 className="text-sm font-medium text-white mb-2">Column Validation</h6>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="summary-item flex justify-between">
                            <span className="summary-label text-gray-300">Columns Checked:</span>
                            <span className="summary-value text-white font-medium">{dbValidation.results.columnsChecked || 0}</span>
                          </div>
                          <div className="summary-item flex justify-between">
                            <span className="summary-label text-gray-300">Columns Valid:</span>
                            <span className="summary-value text-green-400 font-medium">{dbValidation.results.columnsValid || 0}</span>
                          </div>
                          {(dbValidation.results.columnsMissing || 0) > 0 && (
                            <div className="summary-item flex justify-between">
                              <span className="summary-label text-gray-300">Columns Missing:</span>
                              <span className="summary-value text-red-400 font-medium">{dbValidation.results.columnsMissing}</span>
                            </div>
                          )}
                          {(dbValidation.results.columnsMismatched || 0) > 0 && (
                            <div className="summary-item flex justify-between">
                              <span className="summary-label text-gray-300">Columns Mismatched:</span>
                              <span className="summary-value text-yellow-400 font-medium">{dbValidation.results.columnsMismatched}</span>
                            </div>
                          )}
                          {(dbValidation.results.columnsExtra || 0) > 0 && (
                            <div className="summary-item flex justify-between">
                              <span className="summary-label text-gray-300">Extra Columns:</span>
                              <span className="summary-value text-purple-400 font-medium">{dbValidation.results.columnsExtra}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Table List with Details */}
                  {dbValidation.results.tableDetails && Object.keys(dbValidation.results.tableDetails).length > 0 && (
                    <div className="tables-list bg-gray-600 rounded-lg p-4 mb-4">
                      <h5 className="text-md font-medium text-white mb-3">📋 Tables Validation Details</h5>
                      <div className="space-y-2">
                        {Object.entries(dbValidation.results.tableDetails).map(([tableName, tableDetail]) => {
                          const isMissing = !tableDetail.exists;
                          const hasColumnIssues = (tableDetail.columnsMissing || 0) > 0 || (tableDetail.columnsMismatched || 0) > 0;
                          const isExpanded = dbValidation.expandedTables?.includes(tableName);
                          
                          return (
                            <div 
                              key={tableName}
                              className={`table-item bg-gray-700 rounded-lg p-3 border-l-4 cursor-pointer transition-all hover:bg-gray-650 ${
                                isMissing 
                                  ? 'border-red-500' 
                                  : hasColumnIssues 
                                    ? 'border-yellow-500' 
                                    : 'border-green-500'
                              }`}
                              onClick={() => {
                                setDbValidation(prev => ({
                                  ...prev,
                                  expandedTables: isExpanded
                                    ? (prev.expandedTables || []).filter(t => t !== tableName)
                                    : [...(prev.expandedTables || []), tableName]
                                }));
                              }}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                  <span className="text-lg">
                                    {isMissing ? '❌' : hasColumnIssues ? '⚠️' : '✅'}
                                  </span>
                                  <span className="font-medium text-white">{tableName}</span>
                                  {tableDetail.actualTableName && tableDetail.actualTableName !== tableName && (
                                    <span className="text-xs text-gray-400">
                                      (as: {tableDetail.actualTableName})
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center space-x-2">
                                  {isMissing ? (
                                    <span className="text-xs bg-red-600 text-white px-2 py-1 rounded">Missing</span>
                                  ) : (
                                    <>
                                      {tableDetail.columnsValid > 0 && (
                                        <span className="text-xs text-green-400">
                                          {tableDetail.columnsValid} valid
                                        </span>
                                      )}
                                      {tableDetail.columnsMissing > 0 && (
                                        <span className="text-xs bg-yellow-600 text-white px-2 py-1 rounded">
                                          {tableDetail.columnsMissing} missing
                                        </span>
                                      )}
                                      {tableDetail.columnsMismatched > 0 && (
                                        <span className="text-xs bg-orange-600 text-white px-2 py-1 rounded">
                                          {tableDetail.columnsMismatched} mismatched
                                        </span>
                                      )}
                                    </>
                                  )}
                                  <span className="text-gray-400">
                                    {isExpanded ? '▼' : '▶'}
                                  </span>
                                </div>
                              </div>
                              
                              {/* Expanded Details */}
                              {isExpanded && (
                                <div className="mt-3 pt-3 border-t border-gray-500 space-y-2">
                                  {isMissing ? (
                                    <div className="text-sm text-red-300">
                                      <div className="font-semibold mb-1">Table Status:</div>
                                      <div>This table does not exist in the database.</div>
                                      {tableDetail.warnings && tableDetail.warnings.length > 0 && (
                                        <div className="mt-2">
                                          <div className="font-semibold mb-1">Warnings:</div>
                                          <ul className="list-disc list-inside space-y-1">
                                            {tableDetail.warnings.map((warning, idx) => (
                                              <li key={idx} className="text-xs">{warning}</li>
                                            ))}
                                          </ul>
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <>
                                      <div className="text-sm text-gray-300">
                                        <div className="grid grid-cols-2 gap-2 mb-2">
                                          <div>
                                            <span className="text-gray-400">Columns Checked:</span>
                                            <span className="ml-2 text-white">{tableDetail.columnsChecked || 0}</span>
                                          </div>
                                          <div>
                                            <span className="text-gray-400">Columns Valid:</span>
                                            <span className="ml-2 text-green-400">{tableDetail.columnsValid || 0}</span>
                                          </div>
                                          {tableDetail.columnsMissing > 0 && (
                                            <div>
                                              <span className="text-gray-400">Columns Missing:</span>
                                              <span className="ml-2 text-red-400">{tableDetail.columnsMissing}</span>
                                            </div>
                                          )}
                                          {tableDetail.columnsMismatched > 0 && (
                                            <div>
                                              <span className="text-gray-400">Columns Mismatched:</span>
                                              <span className="ml-2 text-yellow-400">{tableDetail.columnsMismatched}</span>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                      
                                      {/* Missing Columns */}
                                      {tableDetail.columnDetails && Object.keys(tableDetail.columnDetails).length > 0 && (
                                        <div className="mt-2">
                                          <div className="text-xs font-semibold text-gray-400 mb-1">Column Details:</div>
                                          <div className="space-y-1 max-h-40 overflow-y-scroll" style={{
                                            scrollbarWidth: 'thin',
                                            scrollbarColor: '#6b7280 #4b5563'
                                          }}>
                                            {Object.entries(tableDetail.columnDetails).map(([colName, colDetail]) => {
                                              if (colDetail.exists && !colDetail.errors?.length && !colDetail.warnings?.length) {
                                                return null; // Skip valid columns to reduce clutter
                                              }
                                              return (
                                                <div 
                                                  key={colName}
                                                  className={`text-xs p-2 rounded ${
                                                    !colDetail.exists 
                                                      ? 'bg-red-900/20 text-red-300 border border-red-700' 
                                                      : colDetail.warnings?.length 
                                                        ? 'bg-yellow-900/20 text-yellow-300 border border-yellow-700'
                                                        : 'bg-green-900/20 text-green-300 border border-green-700'
                                                  }`}
                                                >
                                                  <div className="font-medium">{colName}</div>
                                                  {!colDetail.exists && (
                                                    <div className="text-xs mt-1">Missing</div>
                                                  )}
                                                  {colDetail.warnings && colDetail.warnings.length > 0 && (
                                                    <div className="text-xs mt-1">
                                                      {colDetail.warnings.map((w, idx) => (
                                                        <div key={idx}>⚠️ {w}</div>
                                                      ))}
                                                    </div>
                                                  )}
                                                  {colDetail.errors && colDetail.errors.length > 0 && (
                                                    <div className="text-xs mt-1">
                                                      {colDetail.errors.map((e, idx) => (
                                                        <div key={idx}>❌ {e}</div>
                                                      ))}
                                                    </div>
                                                  )}
                                                </div>
                                              );
                                            })}
                                          </div>
                                        </div>
                                      )}
                                      
                                      {/* Warnings and Errors */}
                                      {(tableDetail.warnings?.length > 0 || tableDetail.errors?.length > 0) && (
                                        <div className="mt-2 space-y-1">
                                          {tableDetail.warnings?.map((warning, idx) => (
                                            <div key={idx} className="text-xs text-yellow-300 bg-yellow-900/20 p-2 rounded">
                                              ⚠️ {warning}
                                            </div>
                                          ))}
                                          {tableDetail.errors?.map((error, idx) => (
                                            <div key={idx} className="text-xs text-red-300 bg-red-900/20 p-2 rounded">
                                              ❌ {error}
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Fix Missing Tables & Columns Button */}
                  {dbValidation.status === 'invalid' && (dbValidation.results.columnsMissing > 0 || dbValidation.results.tablesMissing > 0) && (
                    <div className="fix-columns-actions bg-blue-900/20 border border-blue-700 rounded-lg p-4 mb-4">
                      <h5 className="text-md font-medium text-blue-300 mb-2">🔧 Schema Issues Detected</h5>
                      <p className="text-gray-300 mb-4">
                        {dbValidation.results.tablesMissing > 0 && (
                          <span className="block mb-2">
                            Found <span className="font-bold text-red-300">{dbValidation.results.tablesMissing} missing table(s)</span>.
                          </span>
                        )}
                        {dbValidation.results.columnsMissing > 0 && (
                          <span className="block">
                            Found <span className="font-bold text-yellow-300">{dbValidation.results.columnsMissing} missing column(s)</span>.
                          </span>
                        )}
                        Click below to automatically fix based on schema configuration:
                      </p>
                      <button 
                        className="fix-columns-btn bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                        onClick={handleFixMissingColumns}
                        disabled={dbValidation.isFixing}
                      >
                        {dbValidation.isFixing ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            <span>Fixing Schema...</span>
                          </>
                        ) : (
                          <>
                            <span>🔧</span>
                            <span>Fix Schema Issues</span>
                          </>
                        )}
                      </button>
                      {(dbValidation.results.tablesCreated > 0 || dbValidation.results.columnsFixed > 0) && (
                        <div className="mt-3 text-sm text-green-300 bg-green-900/20 border border-green-700 rounded-lg p-2">
                          ✅ Successfully fixed:
                          {dbValidation.results.tablesCreated > 0 && (
                            <span className="block ml-4">• Created {dbValidation.results.tablesCreated} table(s)</span>
                          )}
                          {dbValidation.results.columnsFixed > 0 && (
                            <span className="block ml-4">• Fixed {dbValidation.results.columnsFixed} column(s)</span>
                          )}
                        </div>
                      )}
                      
                      {/* Show failures if any */}
                      {(dbValidation.results.tablesFailed > 0 || dbValidation.results.columnsFailed > 0) && (
                        <div className="mt-3 text-sm text-red-300 bg-red-900/20 border border-red-700 rounded-lg p-2">
                          ❌ Failed operations:
                          {dbValidation.results.tablesFailed > 0 && (
                            <span className="block ml-4">• Failed to create {dbValidation.results.tablesFailed} table(s)</span>
                          )}
                          {dbValidation.results.columnsFailed > 0 && (
                            <span className="block ml-4">• Failed to fix {dbValidation.results.columnsFailed} column(s)</span>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Detailed Fix Results Report */}
                  {(dbValidation.results.tableResults && Object.keys(dbValidation.results.tableResults).length > 0) && (
                    <div className="fix-results-report bg-gray-700 rounded-lg p-6 mb-4">
                      <h4 className="text-lg font-semibold text-white mb-4">📋 Detailed Fix Results</h4>
                      
                      {Object.entries(dbValidation.results.tableResults).map(([tableName, tableResult]) => {
                        const isTableError = tableResult.status === 'error' || tableResult.status === 'failed';
                        const hasFailures = tableResult.details?.some(d => d.status === 'error' || d.status === 'failed');
                        const hasSuccess = tableResult.details?.some(d => d.status === 'success');
                        
                        return (
                          <div 
                            key={tableName} 
                            className={`table-result mb-4 bg-gray-800 rounded-lg p-4 border-l-4 ${
                              isTableError ? 'border-red-500' : 'border-blue-500'
                            }`}
                          >
                            <h5 className="text-md font-semibold text-white mb-3 flex items-center space-x-2">
                              <span>📊 {tableName}</span>
                              {isTableError && (
                                <span className="text-xs bg-red-600 text-white px-2 py-1 rounded">
                                  Table Creation Failed
                                </span>
                              )}
                              {!isTableError && tableResult.columnsFixed > 0 && (
                                <span className="text-xs bg-green-600 text-white px-2 py-1 rounded">
                                  {tableResult.columnsFixed} fixed
                                </span>
                              )}
                              {!isTableError && tableResult.columnsFailed > 0 && (
                                <span className="text-xs bg-red-600 text-white px-2 py-1 rounded">
                                  {tableResult.columnsFailed} failed
                                </span>
                              )}
                            </h5>
                            
                            {/* Table creation error */}
                            {isTableError && (
                              <div className="table-error-details mb-3">
                                <div className="text-sm text-red-300 bg-red-900/20 border border-red-700 rounded p-3">
                                  <div className="font-semibold mb-1">❌ Table Creation Failed</div>
                                  <div className="text-xs font-mono text-red-400 mt-1 break-words">
                                    {tableResult.error || 'Unknown error occurred while creating table'}
                                  </div>
                                  {tableResult.originalError && (
                                    <div className="text-xs text-orange-400 mt-2 font-mono bg-orange-900/20 p-1 rounded">
                                      Original Error: {tableResult.originalError}
                                    </div>
                                  )}
                                  {tableResult.sqlErrorNumber && (
                                    <div className="text-xs text-yellow-400 mt-1">
                                      SQL Error Number: {tableResult.sqlErrorNumber}
                                    </div>
                                  )}
                                  {tableResult.sql && tableResult.sql !== 'SQL generation failed' && (
                                    <div className="text-xs text-gray-400 mt-2 font-mono bg-gray-900/50 p-2 rounded break-all">
                                      <div className="font-semibold mb-1">SQL Statement:</div>
                                      {tableResult.sql.substring(0, 500)}{tableResult.sql.length > 500 ? '...' : ''}
                                    </div>
                                  )}
                                  {tableResult.sql === 'SQL generation failed' && (
                                    <div className="text-xs text-yellow-400 mt-2 bg-yellow-900/20 p-2 rounded">
                                      ⚠️ Could not generate SQL statement. Check table configuration.
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                            
                            {/* Column fix details */}
                            {tableResult.details && tableResult.details.length > 0 && (
                              <div className="details-list space-y-2">
                                {tableResult.details.map((detail, idx) => (
                                  <div 
                                    key={idx} 
                                    className={`detail-item p-2 rounded text-sm ${
                                      detail.status === 'success' 
                                        ? 'bg-green-900/20 text-green-300 border border-green-700' 
                                        : 'bg-red-900/20 text-red-300 border border-red-700'
                                    }`}
                                  >
                                    <div className="flex items-start justify-between">
                                      <div className="flex-1">
                                        <span className="font-mono font-semibold">{detail.column}</span>
                                        {detail.status === 'success' ? (
                                          <span className="ml-2">✅ {detail.sql || 'Added successfully'}</span>
                                        ) : (
                                          <span className="ml-2">❌ {detail.error || 'Failed to create'}</span>
                                        )}
                                      </div>
                                    </div>
                                    {detail.error && (
                                      <div className="mt-1 text-xs text-red-400 font-mono bg-red-900/30 p-1 rounded break-all">
                                        Error: {detail.error}
                                      </div>
                                    )}
                                    {detail.sql && detail.status === 'success' && (
                                      <div className="mt-1 text-xs text-gray-400 font-mono bg-gray-900/30 p-1 rounded">
                                        SQL: {detail.sql}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Table Creation Errors */}
                  {dbValidation.results.tableErrors && dbValidation.results.tableErrors.length > 0 && (
                    <div className="table-creation-errors bg-red-900/20 border border-red-800 rounded-lg p-4 mb-4">
                      <h5 className="text-md font-medium text-red-400 mb-3">❌ Table Creation Errors:</h5>
                      <ul className="space-y-2">
                        {dbValidation.results.tableErrors.map((error, index) => (
                          <li key={index} className="error-item text-red-300 text-sm">
                            {typeof error === 'string' ? (
                              <>❌ {error}</>
                            ) : (
                              <>❌ {error.table || 'Unknown table'}: {error.error || JSON.stringify(error)}</>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Column Fix Errors */}
                  {dbValidation.results.fixErrors && dbValidation.results.fixErrors.length > 0 && (
                    <div className="column-fix-errors bg-red-900/20 border border-red-800 rounded-lg p-4 mb-4">
                      <h5 className="text-md font-medium text-red-400 mb-3">❌ Column Fix Errors:</h5>
                      <ul className="space-y-2">
                        {dbValidation.results.fixErrors.map((error, index) => (
                          <li key={index} className="error-item text-red-300 text-sm font-mono">
                            ❌ {error}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Add Extra Columns to Schema Button */}
                  {(dbValidation.results.columnsExtra || 0) > 0 && dbValidation.results.extraColumns && (
                    <div className="extra-columns-actions bg-purple-900/20 border border-purple-700 rounded-lg p-4 mb-4">
                      <h5 className="text-md font-medium text-purple-300 mb-2">➕ Extra Columns Detected</h5>
                      <p className="text-gray-300 mb-4">
                        Found {dbValidation.results.columnsExtra} column(s) in database that are not in schema configuration. 
                        Click below to add them to schema configuration:
                      </p>
                      
                      {/* Extra Columns Details */}
                      <div className="extra-columns-list mb-4 max-h-60 overflow-y-scroll bg-gray-800 rounded-lg p-3 space-y-2" style={{
                        scrollbarWidth: 'thin',
                        scrollbarColor: '#6b7280 #374151'
                      }}>
                        {Object.entries(dbValidation.results.extraColumns || {}).map(([tableName, columns]) => (
                          <div key={tableName} className="table-extra-columns border-l-4 border-purple-500 pl-3">
                            <h6 className="text-sm font-semibold text-purple-200 mb-1">📊 {tableName}</h6>
                            <ul className="space-y-1">
                              {Object.entries(columns).map(([columnName, columnInfo]) => (
                                <li key={columnName} className="text-xs text-gray-400 flex items-center justify-between">
                                  <span className="flex items-center space-x-2">
                                    <span className="text-purple-300 font-mono">{columnName}</span>
                                    <span className="text-gray-500">-</span>
                                    <span className="text-gray-400">{columnInfo.definition}</span>
                                  </span>
                                  <div className="flex items-center space-x-2">
                                    <button
                                      className="text-purple-400 hover:text-purple-300 underline text-xs"
                                      onClick={() => handleAddSingleExtraColumn(tableName, columnName, columnInfo.definition)}
                                      disabled={dbValidation.isAddingExtra || dbValidation.isDroppingExtra}
                                    >
                                      Add to Schema
                                    </button>
                                    <span className="text-gray-600">|</span>
                                    <button
                                      className="text-red-400 hover:text-red-300 underline text-xs"
                                      onClick={() => handleDropSingleExtraColumn(tableName, columnName)}
                                      disabled={dbValidation.isAddingExtra || dbValidation.isDroppingExtra}
                                    >
                                      Drop from DB
                                    </button>
                                  </div>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                      
                      <div className="flex space-x-3">
                        <button 
                          className="add-extra-columns-btn bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 flex-1"
                          onClick={handleAddAllExtraColumns}
                          disabled={dbValidation.isAddingExtra || dbValidation.isDroppingExtra}
                        >
                          {dbValidation.isAddingExtra ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                              <span>Adding...</span>
                            </>
                          ) : (
                            <>
                              <span>➕</span>
                              <span>Add All to Schema</span>
                            </>
                          )}
                        </button>
                        <button 
                          className="drop-extra-columns-btn bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 flex-1"
                          onClick={handleDropAllExtraColumns}
                          disabled={dbValidation.isAddingExtra || dbValidation.isDroppingExtra}
                        >
                          {dbValidation.isDroppingExtra ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                              <span>Dropping...</span>
                            </>
                          ) : (
                            <>
                              <span>🗑️</span>
                              <span>Drop All from DB</span>
                            </>
                          )}
                        </button>
                      </div>
                      {dbValidation.results.columnsAddedToSchema > 0 && (
                        <div className="mt-3 text-sm text-green-300 bg-green-900/20 border border-green-700 rounded-lg p-2">
                          ✅ Successfully added {dbValidation.results.columnsAddedToSchema} column(s) to schema configuration
                        </div>
                      )}
                      {dbValidation.results.columnsDropped > 0 && (
                        <div className="mt-3 text-sm text-green-300 bg-green-900/20 border border-green-700 rounded-lg p-2">
                          ✅ Successfully dropped {dbValidation.results.columnsDropped} column(s) from database
                        </div>
                      )}
                    </div>
                  )}

                  {/* Initialize Database Button */}
                  {(dbValidation.status === 'invalid' || dbValidation.status === 'missing') && dbValidation.results.tablesMissing > 0 && (
                    <div className="initialization-actions bg-gray-600 rounded-lg p-4 mb-4">
                      <h5 className="text-md font-medium text-white mb-2">Fix Database Issues</h5>
                      <p className="text-gray-300 mb-4">
                        Found {dbValidation.results.tablesMissing} missing table(s).
                        Click below to create missing tables and insert required seed data:
                      </p>
                      <button 
                        className="initialize-btn bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={handleInitializeDatabase}
                        disabled={dbValidation.isInitializing}
                      >
                        🔧 Initialize Database
                      </button>
                    </div>
                  )}

                  {/* Warnings */}
                  {dbValidation.results.warnings.length > 0 && (
                    <div className="validation-warnings bg-yellow-900/20 border border-yellow-800 rounded-lg p-4 mb-4">
                      <h5 className="text-md font-medium text-yellow-400 mb-3">Warnings:</h5>
                      <ul className="space-y-2">
                        {dbValidation.results.warnings.map((warning, index) => (
                          <li key={index} className="warning-item text-yellow-300 text-sm">⚠️ {warning}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Errors */}
                  {dbValidation.results.errors.length > 0 && (
                    <div className="validation-errors bg-red-900/20 border border-red-800 rounded-lg p-4 mb-4">
                      <h5 className="text-md font-medium text-red-400 mb-3">Errors:</h5>
                      <ul className="space-y-2">
                        {dbValidation.results.errors.map((error, index) => (
                          <li key={index} className="error-item text-red-300 text-sm">❌ {error}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
              )}

              {/* Show message when no validation results yet */}
              {!dbValidation.isValidating && !dbValidation.isInitializing && !dbValidation.status && (
                <div className="right-column flex items-center justify-center w-3/5 p-6 border-l border-gray-600">
                  <div className="text-center text-gray-400">
                    <p className="text-lg mb-2">Validation results will appear here</p>
                    <p className="text-sm">Click "Start Validation" to begin</p>
                  </div>
                </div>
              )}

              {/* Show progress in right column when validating */}
              {(dbValidation.isValidating || dbValidation.isInitializing) && (
                <div className="right-column flex items-center justify-center w-3/5 p-6 border-l border-gray-600">
                  <div className="text-center text-gray-300">
                    <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-lg">
                      {dbValidation.isValidating ? 'Validating Database...' : 'Initializing Database...'}
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="modal-actions flex justify-end p-4 border-t border-gray-600 bg-gray-700 flex-shrink-0">
              <button 
                className="close-btn bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-lg transition-colors font-medium"
                onClick={() => {
                  setActiveModal(null);
                  setDbValidation({
                    isValidating: false,
                    isInitializing: false,
                    isFixing: false,
                    isAddingExtra: false,
                    isDroppingExtra: false,
                    status: null,
                    expandedTables: [],
                    results: {
                      tablesChecked: 0,
                      tablesValid: 0,
                      tablesMissing: 0,
                      tablesCreated: 0,
                      tablesFailed: 0,
                      columnsChecked: 0,
                      columnsValid: 0,
                      columnsMissing: 0,
                      columnsMismatched: 0,
                      columnsExtra: 0,
                      extraColumns: {},
                      columnsFixed: 0,
                      columnsFailed: 0,
                      columnsAddedToSchema: 0,
                      columnsDropped: 0,
                      seedDataInserted: false,
                      errors: [],
                      warnings: [],
                      tableDetails: {},
                      tableResults: {},
                      tableErrors: [],
                      fixErrors: []
                    }
                  });
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Domain Mapping Modal */}
      {emailDomainModal.isOpen && (
        <div className="modal-overlay fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="modal bg-gray-800 border border-gray-700 rounded-lg max-w-2xl w-full">
            <div className="modal-header bg-gray-700 border-b border-gray-600 p-6 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-white">
                {emailDomainModal.isEditing ? 'Edit' : 'Add'} Email Domain Mapping
              </h3>
              <button 
                className="close-btn text-gray-400 hover:text-white p-2 hover:bg-gray-600 rounded-lg transition-colors"
                onClick={() => setEmailDomainModal({ isOpen: false, tenantCode: '', emailDomain: '', isEditing: false })}
              >
                ×
              </button>
            </div>

            <div className="modal-content p-6">
              <div className="mb-6">
                <h4 className="text-lg font-medium text-white mb-2">Tenant: {emailDomainModal.tenantCode}</h4>
                <p className="text-gray-300 text-sm">
                  Users with email addresses from this domain will be automatically assigned to this tenant during registration.
                </p>
              </div>

              <div className="form-group mb-6">
                <label className="block text-sm font-medium text-white mb-2">Email Domain *</label>
                <input
                  type="text"
                  value={emailDomainModal.emailDomain}
                  onChange={(e) => setEmailDomainModal({
                    ...emailDomainModal,
                    emailDomain: e.target.value.toLowerCase()
                  })}
                  placeholder="e.g., worathan.co.th"
                  className="w-full px-3 py-2 border border-gray-500 rounded-lg bg-gray-800 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <small className="text-gray-300 text-xs mt-1">
                  Enter the domain part only (without @). Example: worathan.co.th
                </small>
              </div>

              <div className="modal-actions flex justify-end space-x-3 pt-6 border-t border-gray-600">
                <button 
                  type="button" 
                  className="cancel-btn bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors"
                  onClick={() => setEmailDomainModal({ isOpen: false, tenantCode: '', emailDomain: '', isEditing: false })}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="save-btn bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
                  onClick={() => {
                    if (emailDomainModal.emailDomain) {
                      handleSetEmailDomainMapping(emailDomainModal.tenantCode, emailDomainModal.emailDomain);
                      setEmailDomainModal({ isOpen: false, tenantCode: '', emailDomain: '', isEditing: false });
                    }
                  }}
                >
                  Save Mapping
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Email Domain Test Modal */}
      <div className="modal-overlay fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" style={{ display: 'none' }}>
        <div className="modal bg-gray-800 border border-gray-700 rounded-lg max-w-md w-full">
          <div className="modal-header bg-gray-700 border-b border-gray-600 p-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white">Test Email Domain Mapping</h3>
            <button 
              className="close-btn text-gray-400 hover:text-white p-2 hover:bg-gray-600 rounded-lg transition-colors"
              onClick={() => {
                setTestEmail('');
                setTestResult(null);
              }}
            >
              ×
            </button>
          </div>

          <div className="modal-content p-4">
            <div className="form-group mb-4">
              <label className="block text-sm font-medium text-white mb-2">Test Email Address</label>
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="user@example.com"
                className="w-full px-3 py-2 border border-gray-500 rounded-lg bg-gray-800 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <button 
              type="button" 
              className="test-btn bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors w-full mb-4"
              onClick={handleTestEmailDomainMapping}
            >
              Test Mapping
            </button>

            {testResult && (
              <div className={`test-result p-3 rounded-lg ${
                testResult.success ? 'bg-green-900/20 border border-green-800' : 'bg-red-900/20 border border-red-800'
              }`}>
                <h4 className="font-medium text-white mb-2">Test Result:</h4>
                <p className="text-sm text-gray-300">{testResult.message}</p>
                {testResult.tenant && (
                  <div className="mt-2 p-2 bg-gray-700 rounded">
                    <p className="text-xs text-gray-400">Would be assigned to:</p>
                    <p className="text-sm text-white font-medium">{testResult.tenant.tenantCode} ({testResult.tenant.tenantName})</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Unicode Validation Modal */}
      {activeModal === 'validate-unicode' && selectedTenant && (
        <div className="modal-overlay fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="modal bg-gray-800 border border-gray-700 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="modal-header bg-gray-700 border-b border-gray-600 p-6 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-white">Unicode Validation - {selectedTenant.name}</h3>
              <button 
                className="close-btn text-gray-400 hover:text-white p-2 hover:bg-gray-600 rounded-lg transition-colors"
                onClick={() => {
                  setActiveModal(null);
                  setUnicodeValidation({
                    isValidating: false,
                    isFixing: false,
                    status: null,
                    results: {
                      databaseCollation: '',
                      columnDetails: {},
                      unicodeTestPassed: false,
                      corruptionFound: false,
                      corruptedRecords: [],
                      fixApplied: false,
                      errors: [],
                      warnings: []
                    }
                  });
                }}
              >
                ×
              </button>
            </div>

            <div className="modal-content p-6 space-y-6">
              <div className="unicode-validation-info bg-gray-700 rounded-lg p-6">
                <h4 className="text-lg font-semibold text-white mb-4">Unicode Support Validation</h4>
                <p className="text-gray-300 mb-4">
                  This will check if the tenant's database properly supports Unicode characters (Thai, Chinese, etc.).
                  It will validate column types, test character insertion, and check for data corruption.
                </p>
                
                <div className="validation-details">
                  <h5 className="text-md font-medium text-white mb-3">Validation Process:</h5>
                  <ul className="space-y-2 text-gray-300">
                    <li className="flex items-center space-x-2">✓ <span>Check database collation settings</span></li>
                    <li className="flex items-center space-x-2">✓ <span>Validate column data types (nvarchar vs varchar)</span></li>
                    <li className="flex items-center space-x-2">✓ <span>Test Unicode character insertion</span></li>
                    <li className="flex items-center space-x-2">✓ <span>Check for existing data corruption</span></li>
                    <li className="flex items-center space-x-2">✓ <span>Verify character retrieval accuracy</span></li>
                  </ul>
                </div>
              </div>

              {/* Validation Actions */}
              {!unicodeValidation.isValidating && !unicodeValidation.isFixing && !unicodeValidation.status && (
                <div className="validation-actions bg-gray-700 rounded-lg p-6">
                  <button 
                    className="validate-btn bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={handleValidateUnicode}
                    disabled={loading}
                  >
                    🔍 Validate Unicode Support
                  </button>
                </div>
              )}

              {/* Validation Progress */}
              {unicodeValidation.isValidating && (
                <div className="validation-progress bg-gray-700 rounded-lg p-6">
                  <h4 className="text-lg font-semibold text-white mb-4">Validating Unicode Support...</h4>
                  <div className="progress-spinner flex items-center justify-center mb-4">
                    <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                  <p className="text-gray-300 text-center">Checking database Unicode configuration and testing character support...</p>
                </div>
              )}

              {/* Fix Progress */}
              {unicodeValidation.isFixing && (
                <div className="fix-progress bg-gray-700 rounded-lg p-6">
                  <h4 className="text-lg font-semibold text-white mb-4">Fixing Unicode Schema...</h4>
                  <div className="progress-spinner flex items-center justify-center mb-4">
                    <div className="w-8 h-8 border-4 border-green-600 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                  <p className="text-gray-300 text-center">Converting varchar columns to nvarchar and testing Unicode support...</p>
                </div>
              )}

              {/* Validation Results */}
              {!unicodeValidation.isValidating && !unicodeValidation.isFixing && unicodeValidation.status && (
                <div className="validation-results bg-gray-700 rounded-lg p-6">
                  <h4 className="text-lg font-semibold text-white mb-4">Unicode Validation Results</h4>
                  
                  {unicodeValidation.status === 'valid' && (
                    <div className="result-item success bg-green-900/20 border border-green-800 rounded-lg p-4 mb-4">
                      <div className="flex items-center space-x-3">
                        <span className="result-icon text-2xl">✅</span>
                        <span className="result-text text-green-400 font-medium">
                          Unicode support is working correctly! Database properly handles Thai characters.
                        </span>
                      </div>
                    </div>
                  )}

                  {unicodeValidation.status === 'invalid' && (
                    <div className="result-item warning bg-yellow-900/20 border border-yellow-800 rounded-lg p-4 mb-4">
                      <div className="flex items-center space-x-3">
                        <span className="result-icon text-2xl">⚠️</span>
                        <span className="result-text text-yellow-400 font-medium">
                          Unicode support issues detected! Database may corrupt Thai characters.
                        </span>
                      </div>
                    </div>
                  )}

                  {unicodeValidation.status === 'fixed' && (
                    <div className="result-item success bg-green-900/20 border border-green-800 rounded-lg p-4 mb-4">
                      <div className="flex items-center space-x-3">
                        <span className="result-icon text-2xl">✅</span>
                        <span className="result-text text-green-400 font-medium">
                          Unicode schema fixed successfully! Database now supports Thai characters properly.
                        </span>
                      </div>
                    </div>
                  )}

                  {unicodeValidation.status === 'error' && (
                    <div className="result-item error bg-red-900/20 border border-red-800 rounded-lg p-4 mb-4">
                      <div className="flex items-center space-x-3">
                        <span className="result-icon text-2xl">❌</span>
                        <span className="result-text text-red-400 font-medium">
                          Error occurred during Unicode validation
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Detailed Results */}
                  <div className="detailed-results mt-4 space-y-4">
                    {unicodeValidation.results.databaseCollation && (
                      <div className="result-detail bg-gray-600 rounded-lg p-4">
                        <h5 className="text-md font-medium text-white mb-2">Database Collation:</h5>
                        <p className="text-gray-300">{unicodeValidation.results.databaseCollation}</p>
                      </div>
                    )}

                    {Object.keys(unicodeValidation.results.columnDetails).length > 0 && (
                      <div className="result-detail bg-gray-600 rounded-lg p-4">
                        <h5 className="text-md font-medium text-white mb-2">Column Details:</h5>
                        <div className="space-y-2">
                          {Object.entries(unicodeValidation.results.columnDetails).map(([column, details]) => (
                            <div key={column} className="flex justify-between items-center">
                              <span className="text-gray-300">{column}:</span>
                              <span className={`px-2 py-1 rounded text-sm ${
                                details.isUnicode ? 'bg-green-900/20 text-green-400' : 'bg-red-900/20 text-red-400'
                              }`}>
                                {details.dataType} ({details.isUnicode ? 'Unicode' : 'Non-Unicode'})
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {unicodeValidation.results.corruptionFound && unicodeValidation.results.corruptedRecords.length > 0 && (
                      <div className="result-detail bg-red-900/20 border border-red-800 rounded-lg p-4">
                        <h5 className="text-md font-medium text-red-400 mb-2">Corrupted Records Found:</h5>
                        <div className="space-y-1 max-h-32 overflow-y-auto">
                          {unicodeValidation.results.corruptedRecords.map((record, index) => (
                            <div key={index} className="text-gray-300 text-sm">
                              PersonID {record.PersonID}: "{record.FirstName}" "{record.LastName}"
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {unicodeValidation.results.errors.length > 0 && (
                      <div className="result-detail bg-red-900/20 border border-red-800 rounded-lg p-4">
                        <h5 className="text-md font-medium text-red-400 mb-2">Errors:</h5>
                        <ul className="space-y-1">
                          {unicodeValidation.results.errors.map((error, index) => (
                            <li key={index} className="text-gray-300 text-sm">• {error}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {unicodeValidation.results.warnings.length > 0 && (
                      <div className="result-detail bg-yellow-900/20 border border-yellow-800 rounded-lg p-4">
                        <h5 className="text-md font-medium text-yellow-400 mb-2">Warnings:</h5>
                        <ul className="space-y-1">
                          {unicodeValidation.results.warnings.map((warning, index) => (
                            <li key={index} className="text-gray-300 text-sm">• {warning}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Fix Actions */}
                  {unicodeValidation.status === 'invalid' && !unicodeValidation.isFixing && (
                    <div className="fix-actions mt-6">
                      <button 
                        className="fix-btn bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={handleFixUnicodeSchema}
                        disabled={loading}
                      >
                        🔧 Fix Unicode Schema
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Package Assignment Modal */}
      {showPackageModal && selectedTenantForPackage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-xl max-w-2xl w-full mx-4">
            <div className="flex items-center justify-between p-4 border-b border-gray-600 bg-gray-700">
              <h2 className="text-xl font-semibold text-white">
                Assign Package - {selectedTenantForPackage.tenantName || selectedTenantForPackage.name}
              </h2>
              <button
                onClick={() => {
                  setShowPackageModal(false);
                  setSelectedTenantForPackage(null);
                }}
                className="p-2 hover:bg-gray-600 rounded-lg text-gray-400 hover:text-white transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6">
              <div className="mb-4">
                <p className="text-gray-300 text-sm mb-4">
                  Current Package: {tenantPackages[selectedTenantForPackage.code]?.package?.packageName || 'No Package Assigned'}
                </p>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Select Package
                </label>
                <select
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                  onChange={(e) => {
                    if (e.target.value) {
                      handleAssignPackage(selectedTenantForPackage.id || selectedTenantForPackage.tenant_id, parseInt(e.target.value));
                    }
                  }}
                  defaultValue=""
                >
                  <option value="">Select a package...</option>
                  {packages.map(pkg => (
                    <option key={pkg.packageId} value={pkg.packageId}>
                      {pkg.packageName} ({pkg.packageCode})
                    </option>
                  ))}
                </select>
              </div>
              {tenantPackages[selectedTenantForPackage.code] && (
                <div className="mt-4 p-4 bg-gray-700 rounded-lg">
                  <h3 className="text-sm font-medium text-gray-300 mb-2">Current Package Details</h3>
                  <div className="text-xs text-gray-400 space-y-1">
                    <p>Package: {tenantPackages[selectedTenantForPackage.code].package.packageName}</p>
                    <p>Assigned: {new Date(tenantPackages[selectedTenantForPackage.code].assignedAt).toLocaleDateString()}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* AI Configuration Modal */}
      {showAiConfigModal && selectedTenantForAiConfig && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-600 bg-gray-700">
              <h2 className="text-xl font-semibold text-white flex items-center space-x-2">
                <Brain className="h-6 w-6 text-blue-400" />
                <span>AI Configuration - {selectedTenantForAiConfig.tenantName}</span>
              </h2>
              <button
                onClick={() => {
                  setShowAiConfigModal(false);
                  setSelectedTenantForAiConfig(null);
                }}
                className="p-2 hover:bg-gray-600 rounded-lg text-gray-400 hover:text-white transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
              <TenantConfiguration tenantCode={selectedTenantForAiConfig.code} isModal={true} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TenantManagement;